import React from "react";
import { compareOperationalRecords } from "../lib/sortUtils";
import { buildNextRecordNumber, mapCostCenterCodes } from "../lib/recordNumberUtils";
import { isAdminNotificationEnabled } from "../lib/adminNotificationPrefs";
import { buildProviderDirectory } from "../lib/peopleRoleAdapters";
import { choosePreferredOperationalRecord, isOperationalRecordDeleted } from "../lib/operationalRecordUtils";
import { fetchClimateAlertsBase, fetchClimateForecastBase, fetchFarmClimateSummary } from "../lib/climateApi";
import { getFarmClimateProfile, normalizeClimateStations } from "../lib/climateConfig";
import { buildOperationalClimatePanel, climateLevelMeta, resolveOperationalClimateContext } from "../lib/climateOperationalContext";
import { LIVE_ACTIVITY_EVENT_TYPES, LIVE_ACTIVITY_MODULES, notifyOperationalActivity } from "../lib/liveActivities";
import { stampRecordGeneration } from "../lib/farmGenerations";
import { appendProvisionalSuffix, appendUpdatePendingDisplaySuffix, markOperationalCreatePending, markOperationalUpdatePending } from "../lib/operationalProvisional";
import { getLiveCollectionCache } from "../sync/cacheApi";
import { DB } from "../lib/deviceStorage";

export function createOccurrencesModule(deps){
  const {
    useState,
    useEffect,
    useSysLists,
    useSync,
    useFarmCollection,
    useGlobalCollection,
    useFarm,
    useToast,
    useReliableSyncOnSave,
    canCreateInModule,
    canEditInModule,
    canDeleteInModule,
    canViewCostCenter,
    canEditCostCenter,
    getAllowedCostCentersForModule,
    itemMatchesCostCenterAccess,
    itemCostCenters,
    SYSTEM_LISTS_DEFAULTS,
    enrichStockItemsWithQty,
    normalizePhotoEntries,
    getMaterialUsageQty,
    getRestockableMaterials,
    hasPendingMaterialRestock,
    hasCompletedMaterialRestock,
    getPriorityConfig,
    getPriorities,
    getAccounts,
    createClientId,
    today,
    fmtMoney,
    fmtDate,
    fmtDateTime,
    daysSince,
    T,
    isCompactViewport,
    Btn,
    Field,
    Input,
    Select,
    Textarea,
    StatCard,
    Toast,
    Badge,
    ServicePhotoAddPhoneIcon,
    ServicePhotoStatusIcon,
    OCCURRENCE_CATEGORIES,
    OCCURRENCE_STATUS,
    makeCreateMeta,
    makeUpdateMeta,
  } = deps;

  return function OccurrencesModule({ session, isGestor, openOccurrenceId, onClearOpenOccurrence, onBridgeBack }) {
  const useSyncHook = typeof useSync === "function" ? useSync : function useNoSyncContext(){ return null; };
  const syncCtx = useSyncHook();
  const { fid: currentFarmId, farm: currentFarm } = useFarm();
  const [occurrences, setOccurrences] = useFarmCollection("occurrences", []);
  const [providers,  ]                = useGlobalCollection("providers",   []);
  const [people,     ]                = useGlobalCollection("people",      []);
  const [employees,  ]                = useFarmCollection("employees",   []);
  const [pastures,   ]                = useFarmCollection("pastures",    []);
  const [pois,       ]                = useFarmCollection("poi",        []);
  const [machinery,  ]                = useFarmCollection("machinery",   []);
  const [stockItems, ]                = useGlobalCollection("stock",     []);
  const [adminGlobalsConfigRows]      = useGlobalCollection("adminGlobalsConfig", []);
  const adminGlobalsConfig            = React.useMemo(()=> (adminGlobalsConfigRows||[])[0] || null, [adminGlobalsConfigRows]);
  const climateProfiles               = React.useMemo(()=> adminGlobalsConfig?.farmClimateProfiles || {}, [adminGlobalsConfig]);
  const farmClimateProfile            = React.useMemo(()=> getFarmClimateProfile(climateProfiles, currentFarmId), [climateProfiles, currentFarmId]);
  const climateStationCandidates      = React.useMemo(()=> normalizeClimateStations(farmClimateProfile?.stations, farmClimateProfile), [farmClimateProfile]);
  const providerDirectory             = React.useMemo(()=>buildProviderDirectory(people, providers), [people, providers]);
  const [stockMovs, setStockMovs]     = useFarmCollection("stockMovements", []);
  const [, setNotifications]          = useFarmCollection("notifications", []);
  const lists = useSysLists();
  const costCenterCodeMap = React.useMemo(()=>mapCostCenterCodes(lists?.COST_CENTER_CODES || SYSTEM_LISTS_DEFAULTS.COST_CENTER_CODES || []), [lists]);
  const occTitleOptions = lists?.SERVICE_TITLES || SYSTEM_LISTS_DEFAULTS.SERVICE_TITLES || [];
  const execTypeOptions = lists?.SERVICE_EXEC_TYPES || SYSTEM_LISTS_DEFAULTS.SERVICE_EXEC_TYPES || ["Interno (Colaborador)","Externo (Prestador)","Misto (Colaborador + Prestador)"];
  const pauseReasonOptions = lists?.TASK_PAUSE_REASONS || SYSTEM_LISTS_DEFAULTS.TASK_PAUSE_REASONS || [];
  const [form,        setForm]        = useState(null);
  const [detailOcc,   setDetailOcc]   = useState(null);
  const [detailClimatePanel, setDetailClimatePanel] = useState(null);
  const [detailClimateLoading, setDetailClimateLoading] = useState(false);
  const [formClimatePanel, setFormClimatePanel] = useState(null);
  const [formClimateLoading, setFormClimateLoading] = useState(false);
  const [occMatForm,  setOccMatForm]  = useState(null);
  const [occMaterialUsageDrafts, setOccMaterialUsageDrafts] = useState({});
  const [occPauseForm,setOccPauseForm]= useState(null);
  const [occPhotoForm, setOccPhotoForm] = useState(null);
  const [occPhotoLightbox, setOccPhotoLightbox] = useState(null);
  const [occRestockForm, setOccRestockForm] = useState(null);
  const [occSchedDate,setOccSchedDate]= useState(today());
  const [filterStatus,setFilterStatus]= useState("Todos");
  const [filterCat,   setFilterCat]   = useState("Todos");
  const [cardFilter,  setCardFilter]  = useState("all");
  const [toast,       show]           = useToast();
  const onClearOpenOccurrenceRef = React.useRef(onClearOpenOccurrence);
  React.useEffect(()=>{ onClearOpenOccurrenceRef.current = onClearOpenOccurrence; }, [onClearOpenOccurrence]);
  const consumedOpenOccurrenceIdRef = React.useRef(null);
  // v3.6.02: dedup do fallback pós-ACK de Live — evita disparar várias vezes
  // para o mesmo recordId/recordNumber dentro de 45s.
  const occurrenceLiveAckFallbackRef = React.useRef({});


  const renderClimateToneBadge = React.useCallback((tone, label, value)=>{
    const meta = climateLevelMeta(tone === "restrictive" ? "restrictive" : (tone === "favorable" ? "favorable" : "attention"));
    return <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:999,border:`1px solid ${meta.color}44`,background:`${meta.color}14`,color:meta.color,fontSize:11,fontWeight:700}}>{label}{value?`: ${value}`:""}</span>;
  }, []);

  const loadOperationalClimatePanel = React.useCallback(async (record, setter, loadingSetter) => {
    if (!currentFarmId || !record) { setter(null); return; }
    const context = resolveOperationalClimateContext(record, pastures, pois);
    loadingSetter(true);
    try {
      const requestBase = {
        farmId: currentFarmId,
        preferredSource: farmClimateProfile?.preferredSource || "wunderground",
        fallbackToInmet: farmClimateProfile?.fallbackToInmet !== false,
        latitude: context?.latitude,
        longitude: context?.longitude,
        stationCandidates: climateStationCandidates,
        contextType: context?.contextType || "farm",
        contextId: context?.contextId || "",
        contextName: context?.contextName || record?.location || record?.title || "",
        autoSync: true,
        userName: session?.name || "",
      };
      const [summary, forecast, alerts] = await Promise.all([
        fetchFarmClimateSummary({ ...requestBase, windowDays: farmClimateProfile?.analysisWindowDays || 30 }),
        fetchClimateForecastBase(requestBase),
        fetchClimateAlertsBase({ ...requestBase, alertRules: {
          heat_enabled: farmClimateProfile?.alertsHeatEnabled,
          heat_warning_temp_c: farmClimateProfile?.alertsHeatWarningTempC,
          heat_critical_temp_c: farmClimateProfile?.alertsHeatCriticalTempC,
          low_humidity_enabled: farmClimateProfile?.alertsLowHumidityEnabled,
          low_humidity_warning_pct: farmClimateProfile?.alertsLowHumidityWarningPct,
          low_humidity_critical_pct: farmClimateProfile?.alertsLowHumidityCriticalPct,
          heavy_rain_enabled: farmClimateProfile?.alertsHeavyRainEnabled,
          heavy_rain_warning_mm: farmClimateProfile?.alertsHeavyRainWarningMm,
          heavy_rain_critical_mm: farmClimateProfile?.alertsHeavyRainCriticalMm,
          dry_streak_enabled: farmClimateProfile?.alertsDryStreakEnabled,
          dry_streak_warning_days: farmClimateProfile?.alertsDryStreakWarningDays,
          dry_streak_critical_days: farmClimateProfile?.alertsDryStreakCriticalDays,
        } }),
      ]);
      setter(buildOperationalClimatePanel({ summary, forecast, alerts, profile: farmClimateProfile, contextName: context?.contextName || record?.location || record?.title || "Fazenda" }));
    } catch (err) {
      setter({ error: err?.message || "Não foi possível consultar o clima operacional agora." });
    } finally {
      loadingSetter(false);
    }
  }, [currentFarmId, pastures, pois, farmClimateProfile, climateStationCandidates, session?.name]);

  React.useEffect(()=>{
    if(!detailOcc) { setDetailClimatePanel(null); setDetailClimateLoading(false); return; }
    loadOperationalClimatePanel(detailOcc, setDetailClimatePanel, setDetailClimateLoading);
  }, [detailOcc, loadOperationalClimatePanel]);

  React.useEffect(()=>{
    if(!form) { setFormClimatePanel(null); setFormClimateLoading(false); return; }
    if(!(form.location || form.poiId)) { setFormClimatePanel(null); setFormClimateLoading(false); return; }
    loadOperationalClimatePanel(form, setFormClimatePanel, setFormClimateLoading);
  }, [form, loadOperationalClimatePanel]);
  const syncOnSave = useReliableSyncOnSave();
  const myName     = session?.name || "";
  const bridgeCostCenter = String(session?.sedeBridge?.enabled ? (session?.sedeBridge?.costCenter || "") : "").trim();
  const bridgeScoped = !!bridgeCostCenter;
  const matchesBridgeCostCenter = React.useCallback((item)=> !bridgeScoped || String(item?.costCenter || "").trim().toLowerCase()===bridgeCostCenter.toLowerCase(), [bridgeScoped, bridgeCostCenter]);
  const safeItemMatchesCostCenterAccess = React.useCallback((sess, moduleId, item)=> (typeof itemMatchesCostCenterAccess === "function" ? itemMatchesCostCenterAccess(sess, moduleId, item) : true), []);
  const normalizeTimeline = React.useCallback((timeline)=> (Array.isArray(timeline)?timeline:[]).filter(Boolean), []);
  const closedOccurrenceStatuses = new Set(["Concluída","Cancelada","Concluído","Cancelado"]);
  const getOccurrenceMaintenanceAnchor = (occ, fallbackTs=new Date().toISOString()) => (
    occ?.maintenanceStartedAt || occ?.startedAt || occ?.date || occ?.reportedAt || occ?.createdAt || fallbackTs
  );
  const normalizeOccurrencePauseHistory = (occ) => Array.isArray(occ?.pauseHistory)
    ? occ.pauseHistory.filter(Boolean).map(entry=>({ ...entry }))
    : [];
  function buildOccurrenceMaintenancePatch(currentOcc, patch={}) {
    const now = new Date().toISOString();
    const nextStatus = String(patch.status ?? currentOcc?.status ?? "").trim();
    const next = { ...patch };
    const pauseHistory = normalizeOccurrencePauseHistory(currentOcc);
    const anchor = getOccurrenceMaintenanceAnchor({ ...currentOcc, ...patch }, patch.startedAt || patch.completedAt || patch.completionDate || now);
    if ((["Em Execução","Pausada","Concluída","Concluído"].includes(nextStatus) || !!patch.startedAt) && !currentOcc?.maintenanceStartedAt) {
      next.maintenanceStartedAt = anchor;
    }
    if (nextStatus === "Pausada") {
      const pauseStartedAt = patch.pausedAt || currentOcc?.pausedAt || now;
      const last = pauseHistory[pauseHistory.length - 1];
      if (!last || last.endedAt) {
        pauseHistory.push({
          startedAt: pauseStartedAt,
          reason: patch.pauseReason || currentOcc?.pauseReason || "",
          note: patch.pauseNote || currentOcc?.pauseNote || "",
          actor: myName,
        });
      } else {
        last.reason = patch.pauseReason || last.reason || currentOcc?.pauseReason || "";
        last.note = patch.pauseNote || last.note || currentOcc?.pauseNote || "";
        last.actor = last.actor || myName;
      }
      next.pauseHistory = pauseHistory;
    }
    if (nextStatus === "Em Execução") {
      const resumeAt = patch.resumedAt || now;
      const last = pauseHistory[pauseHistory.length - 1];
      if (last && !last.endedAt) last.endedAt = resumeAt;
      next.pauseHistory = pauseHistory;
      next.pausedAt = "";
    }
    if (closedOccurrenceStatuses.has(nextStatus) || patch.completedAt || patch.completionDate) {
      const endAt = patch.completedAt || patch.completionDate || currentOcc?.completedAt || currentOcc?.completionDate || now;
      const last = pauseHistory[pauseHistory.length - 1];
      if (last && !last.endedAt) last.endedAt = endAt;
      next.pauseHistory = pauseHistory;
      if (!next.maintenanceCompletedAt) next.maintenanceCompletedAt = endAt;
      if (!next.maintenanceStartedAt) next.maintenanceStartedAt = anchor;
    }
    return next;
  }
  const buildOccurrenceNotificationRecipients = React.useCallback(async (occRecord)=>{
    const loadedAccounts = await getAccounts?.();
    const accounts = Array.isArray(loadedAccounts) ? loadedAccounts : [];
    const farmId = String(occRecord?.farmId || currentFarmId || "").trim();
    const creatorUsername = String(session?.username || "").trim().toLowerCase();
    const creatorAccountId = String(session?.id || "").trim();
    const canAccessFarm = (acc)=>{
      const farmIds = Array.isArray(acc?.farmIds) ? acc.farmIds.filter(Boolean) : [];
      return !farmId || !farmIds.length || farmIds.includes(farmId);
    };
    const recipientsMap = new Map();
    const pushRecipient = (acc, roleOverride="")=>{
      if(!acc) return;
      const accountId = String(acc?.id || "").trim();
      const username = String(acc?.username || "").trim().toLowerCase();
      if(!accountId && !username) return;
      const key = accountId || username;
      if(recipientsMap.has(key)) return;
      recipientsMap.set(key, {
        accountId,
        userId: String(acc?.userId || "").trim(),
        username,
        role: String(roleOverride || acc?.role || "").trim().toLowerCase(),
        name: String(acc?.name || "").trim(),
      });
    };
    pushRecipient({ id: creatorAccountId, userId: session?.userId || "", username: creatorUsername, role: session?.role || "solicitante", name: session?.name || occRecord?.reportedBy || "" }, session?.role || "solicitante");
    accounts.filter(acc=>String(acc?.role || "").trim().toLowerCase()==="admin" && acc?.active!==false).forEach(acc=>pushRecipient(acc, "admin"));
    accounts.filter(acc=>String(acc?.role || "").trim().toLowerCase()==="gestor" && acc?.active!==false && canAccessFarm(acc)).forEach(acc=>pushRecipient(acc, "gestor"));
    return Array.from(recipientsMap.values());
  }, [currentFarmId, session?.id, session?.name, session?.role, session?.userId, session?.username]);


  const buildOccurrenceLiveActivityDisplay = React.useCallback((occRecord={}, meta={})=>{
    const priorityMeta = getPriorityConfig("occurrence", occRecord?.priority || meta?.priority || "normal");
    const stepperLabels = ["Aberta", "Análise", "Execução", "Pausa", "Concluída"];
    const statusText = String(occRecord?.status || meta?.status || "Aberta").trim();
    const stepperCurrent = statusText.includes("Concl") || statusText.includes("Cancel") || statusText.includes("Resol") || statusText.includes("Final") ? "Concluída"
      : statusText.includes("Paus") || statusText.includes("Susp") || statusText.includes("Aguard") ? "Pausa"
      : statusText.includes("Execução") || statusText.includes("Exec") || statusText.includes("Agend") ? "Execução"
      : statusText.includes("Análise") || statusText.includes("Analise") || statusText.includes("Trat") || statusText.includes("Reparo") || statusText.includes("Peça") || statusText.includes("Aprovação") || statusText.includes("Orçamento") ? "Análise"
      : "Aberta";
    return {
      displayTitle: "Trembo - Ocorrência",
      displayBadge: priorityMeta?.label || "Normal",
      recordCode: occRecord?.recordNumber || occRecord?.id || "",
      displayDate: fmtDate(occRecord?.date || occRecord?.createdAt || occRecord?.updatedAt || ""),
      costCenterName: occRecord?.costCenter || "",
      stepperLabels,
      stepperCurrent,
    };
  }, []);

  const appendOccurrenceNotification = React.useCallback(async (occRecord, meta={})=>{
    try{
      occRecord = stampRecordGeneration(occRecord, currentFarm);
      const actor = session?.name || occRecord?.reportedBy || "Sistema";
      const eventAt = String(meta?.eventAt || occRecord?.updatedAt || occRecord?.createdAt || new Date().toISOString()).trim() || new Date().toISOString();
      const liveDisplay = buildOccurrenceLiveActivityDisplay(occRecord, meta);
      const liveSettingsFromAdmin = adminGlobalsConfig?.liveActivitySettings;
      const liveGloballyDisabled = liveSettingsFromAdmin?.enabled === false;
      const liveModuleDisabled = liveSettingsFromAdmin?.modules?.[LIVE_ACTIVITY_MODULES.OCCURRENCES] === false;
      const occurrenceHasPendingOperation = !!(
        occRecord?.pendingLocalCreate ||
        occRecord?.pendingLocalUpdate ||
        occRecord?.pendingLocalDelete ||
        occRecord?.pendingLocalChange ||
        occRecord?.updatePending ||
        String(occRecord?.recordNumber || "").includes("-PRV")
      );
      // v3.6.00: Ocorrências na lane operacional seguem services/orders.
      // Live local não inicia com número PRV; o servidor dispara após push_ops + ACK oficial.
      if(!liveGloballyDisabled && !liveModuleDisabled && !occurrenceHasPendingOperation) notifyOperationalActivity({
        module: LIVE_ACTIVITY_MODULES.OCCURRENCES,
        eventType: String(meta?.event || "").includes("created") ? LIVE_ACTIVITY_EVENT_TYPES.CREATED : LIVE_ACTIVITY_EVENT_TYPES.STATUS_CHANGED,
        record: occRecord,
        title: liveDisplay.displayTitle || String(meta?.title || "Atualização de ocorrência").trim() || "Atualização de ocorrência",
        status: meta?.status || occRecord?.status || "Aberta",
        previousStatus: meta?.previousStatus || "",
        farmId: String(occRecord?.farmId || currentFarmId || "").trim(),
        farmName: occRecord?.farmName || currentFarm?.name || "",
        extraFarmShortCode: currentFarm?.shortCode || currentFarm?.sigla || occRecord?.farmShortCode || occRecord?.farmSigla || "",
        updatedAt: eventAt,
        actor,
        priority: occRecord?.priority || meta?.priority || "normal",
        ...liveDisplay,
        extra: { event: String(meta?.event || "occurrence_updated").trim() || "occurrence_updated", eventLabel: String(meta?.eventLabel || "Atualização da ocorrência").trim() || "Atualização da ocorrência", recordNumber: occRecord?.recordNumber || "", ...liveDisplay },
      })?.catch?.(() => {});
      if(!isAdminNotificationEnabled(adminGlobalsConfig, "occurrences")) return;
      const recipients = await buildOccurrenceNotificationRecipients(occRecord);
      if(!recipients.length) return;
      const summary = String(meta?.summary || meta?.body || "").trim() || `${actor} atualizou a ocorrência${occRecord?.costCenter ? ` · ${occRecord.costCenter}` : ""}.`;
      const notifId = createClientId("ntf");
      const notification = {
        id:notifId,
        clientId:notifId,
        farmId:String(occRecord?.farmId || currentFarmId || "").trim(),
        type:"occurrence_event",
        event:String(meta?.event || "occurrence_updated").trim() || "occurrence_updated",
        eventLabel:String(meta?.eventLabel || "Atualização da ocorrência").trim() || "Atualização da ocorrência",
        title:String(meta?.title || "Atualização de ocorrência").trim() || "Atualização de ocorrência",
        summary,
        body:String(meta?.body || summary).trim() || summary,
        entityType:"occurrence",
        entityId: occRecord?.id || "",
        entityRecordNumber: occRecord?.recordNumber || "",
        orderId: occRecord?.id || "",
        orderRecordNumber: occRecord?.recordNumber || "",
        notificationModuleId:"occurrences",
        notificationSectionKey:"occurrences",
        status: meta?.status || occRecord?.status || "Aberta",
        orderStatus: meta?.status || occRecord?.status || "Aberta",
        previousOrderStatus: meta?.previousStatus || "",
        costCenter: occRecord?.costCenter || "",
        requester: occRecord?.reportedBy || actor,
        recipients,
        openedInApp:false,
        readBy:[],
        eventAt,
        ...makeCreateMeta(session?.name || "", { updatedAt:eventAt }),
      };
      setNotifications(prev=>[...(Array.isArray(prev)?prev:[]), notification]);
      setTimeout(()=>{
        try{ syncOnSave("notifications"); }catch(_e){}
      }, 80);
    }catch(e){}
  }, [adminGlobalsConfig, buildOccurrenceLiveActivityDisplay, buildOccurrenceNotificationRecipients, currentFarm, currentFarmId, session?.name, setNotifications, syncOnSave]);

  async function notifyOccurrenceStatusChange(previousOcc, nextOcc, eventMeta={}){
    const previousStatus = String(previousOcc?.status || "").trim();
    const nextStatus = String(nextOcc?.status || "").trim();
    if(!nextStatus || previousStatus === nextStatus) return;
    const actor = session?.name || nextOcc?.reportedBy || "Sistema";
    const recordLabel = nextOcc?.recordNumber || nextOcc?.id || "";
    const defaultSummary = `${actor} atualizou a ocorrência ${recordLabel} de ${previousStatus || "sem status"} para ${nextStatus}.`;
    await appendOccurrenceNotification(nextOcc, {
      event:eventMeta?.event || "occurrence_status_changed",
      eventLabel:eventMeta?.eventLabel || "Status atualizado",
      title:eventMeta?.title || `Ocorrência ${nextStatus}`,
      summary:eventMeta?.summary || defaultSummary,
      body:eventMeta?.body || eventMeta?.summary || defaultSummary,
      status:nextStatus,
      previousStatus,
      eventAt:eventMeta?.eventAt || nextOcc?.updatedAt || new Date().toISOString(),
    });
  }

  const dedupeCollection = React.useCallback((items, keyResolvers)=>{
    const list = Array.isArray(items) ? items.filter(Boolean) : [];
    const seen = new Map();
    list.forEach((item, index)=>{
      const keys = keyResolvers.map((fn)=>{
        try { return fn(item); } catch { return ""; }
      }).filter(Boolean);
      const key = keys[0] || `__idx_${index}`;
      const prev = seen.get(key);
      if(!prev) { seen.set(key, item); return; }
      seen.set(key, choosePreferredOperationalRecord(prev, item));
    });
    return Array.from(seen.values());
  }, []);
  const normalizeOccurrence = React.useCallback((occ)=>{
    if(!occ) return null;
    const stableId = String(occ.id || createClientId("occ"));
    const farmId = occ.farmId || currentFarmId || "";
    return {
      ...occ,
      id: stableId,
      clientId: String(occ.clientId || stableId),
      farmId,
      title: String(occ.title || occ.description || "Ocorrência"),
      location: String(typeof occ.location === "string" ? occ.location : (occ.location?.name || occ.location?.label || occ.location || "")).trim(),
      assignedTo: String(typeof occ.assignedTo === "string" ? occ.assignedTo : (occ.assignedTo?.name || occ.assignedTo?.displayName || occ.assignedTo?.username || occ.assignedTo || "")).trim(),
      reportedBy: String(typeof occ.reportedBy === "string" ? occ.reportedBy : (occ.reportedBy?.name || occ.reportedBy?.displayName || occ.reportedBy || "")).trim(),
      costCenter: occ.costCenter || bridgeCostCenter || "Sede",
      costCenters: itemCostCenters(occ, occ.costCenter || bridgeCostCenter || "Sede"),
      timeline: normalizeTimeline(occ.timeline),
      materials: Array.isArray(occ.materials) ? occ.materials.filter(Boolean) : [],
      assignees: Array.isArray(occ.assignees) ? occ.assignees.filter(Boolean).map(a=> typeof a === "string" ? { name:a } : a) : [],
      photos: normalizePhotoEntries(Array.isArray(occ.photos) ? occ.photos : []),
      occurrencePhotos: normalizePhotoEntries(Array.isArray(occ.occurrencePhotos) ? occ.occurrencePhotos : []),
      status: occ.status || "Aberta",
      _deleted: !!(occ?._deleted || occ?.deletedAt || occ?.deleted_at),
      deletedAt: occ?.deletedAt || occ?.deleted_at || "",
      deleted_at: occ?.deletedAt || occ?.deleted_at || "",
      ...(!occ.createdAt ? makeCreateMeta(myName) : {}),
      ...(!occ.updatedAt ? { updatedAt: occ.createdAt || new Date().toISOString(), updatedBy: occ.updatedBy || myName } : {}),
    };
  }, [currentFarmId, myName, normalizeTimeline, bridgeCostCenter]);
  const normalizeOccurrencesCollection = React.useCallback((items)=> dedupeCollection((Array.isArray(items)?items:[]).map(normalizeOccurrence).filter(Boolean), [
    (occ)=>String(occ.clientId||""),
    (occ)=>String(occ.id||""),
    (occ)=>`${String(occ.title||occ.description||"").trim().toLowerCase()}__${String(occ.location||"").trim().toLowerCase()}__${String(occ.reportedAt||occ.createdAt||"")}`,
  ]), [dedupeCollection, normalizeOccurrence]);
  const makeProvisionalOccurrenceNumber = React.useCallback((records, costCenter)=>{
    const next = buildNextRecordNumber(Array.isArray(records) ? records.filter(Boolean) : [], { moduleKey:"occurrences", costCenter, costCenterCodeMap });
    return {
      ...next,
      recordNumber: appendProvisionalSuffix(next.recordNumber),
      recordNumberStatus: "provisional",
    };
  }, [costCenterCodeMap]);

  const displayOccurrenceRecordNumber = React.useCallback((record)=>{
    if(!record) return "—";
    const rn = String(record.recordNumber || record.id || "").trim();
    if(!rn) return "—";
    if(record.pendingLocalUpdate && !record.pendingLocalCreate) return appendUpdatePendingDisplaySuffix(rn);
    return rn;
  }, []);

  const makeOccurrenceTimelineEntry = React.useCallback((entry = {}, at = null)=>{
    const stableId = String(entry.clientId || entry.id || createClientId("otl"));
    return {
      ...entry,
      id: String(entry.id || stableId),
      clientId: stableId,
      ts: String(entry.ts || at || new Date().toISOString()),
      actor: String(entry.actor || myName || "Sistema"),
    };
  }, [myName]);

  const makeOccurrenceMaterialEntry = React.useCallback((entry = {}, at = null)=>{
    const stableId = String(entry.clientId || entry.id || createClientId("omat"));
    return {
      ...entry,
      id: String(entry.id || stableId),
      clientId: stableId,
      ts: String(entry.ts || at || new Date().toISOString()),
      by: String(entry.by || myName || "Sistema"),
    };
  }, [myName]);

  const makeOccurrenceStockMovementEntry = React.useCallback((entry = {}, at = null)=>{
    const stableId = String(entry.clientId || entry.id || createClientId("smv"));
    return {
      ...entry,
      id: String(entry.id || stableId),
      clientId: stableId,
      ts: String(entry.ts || at || new Date().toISOString()),
      updatedAt: String(entry.updatedAt || entry.ts || at || new Date().toISOString()),
      by: String(entry.by || myName || "Sistema"),
    };
  }, [myName]);

  const ensureOccurrenceRecordNumbers = React.useCallback((records)=>{
    const baseRecords = Array.isArray(records) ? records.slice() : [];
    const sorted = baseRecords.map((record, index)=>({ record, index, stamp: String(record?.createdAt || record?.updatedAt || index) }))
      .sort((a,b)=> String(a.stamp).localeCompare(String(b.stamp)));
    let changed = false;
    const working = baseRecords.slice();
    sorted.forEach(({ record, index })=>{
      if (!record || record.recordNumber) return;
      const next = buildNextRecordNumber(working.filter(Boolean), { moduleKey:"occurrences", costCenter:record?.costCenter, costCenterCodeMap });
      working[index] = { ...record, ...next };
      changed = true;
    });
    return changed ? working : baseRecords;
  }, [costCenterCodeMap]);
  const normalizeStockMovementRecord = React.useCallback((mov)=>{
    if(!mov) return null;
    const stableId = String(mov.clientId || mov.id || createClientId("smv"));
    return {
      ...mov,
      id: String(mov.id || stableId),
      clientId: stableId,
      farmId: mov.farmId || currentFarmId || "",
      qty: Number(mov.qty || 0),
      updatedAt: mov.updatedAt || mov.ts || mov.createdAt || new Date().toISOString(),
      by: mov.by || myName,
    };
  }, [currentFarmId, myName]);
  const normalizeStockMovementsCollection = React.useCallback((items)=> dedupeCollection((Array.isArray(items)?items:[]).map(normalizeStockMovementRecord).filter(Boolean), [
    (mov)=>String(mov.clientId||""),
    (mov)=>String(mov.id||""),
    (mov)=>`${String(mov.itemId||"")}__${String(mov.origin||"")}__${String(mov.originId||"")}__${String(mov.type||"")}__${String(mov.ts||mov.updatedAt||"")}`,
  ]), [dedupeCollection, normalizeStockMovementRecord]);
  const applyOccurrences = React.useCallback((updater)=>{
    setOccurrences(prev=>ensureOccurrenceRecordNumbers(normalizeOccurrencesCollection(typeof updater === "function" ? updater(Array.isArray(prev)?prev:[]) : updater)));
  }, [normalizeOccurrencesCollection, ensureOccurrenceRecordNumbers, setOccurrences]);
  const applyStockMovements = React.useCallback((updater)=>{
    setStockMovs(prev=>normalizeStockMovementsCollection(typeof updater === "function" ? updater(Array.isArray(prev)?prev:[]) : updater));
  }, [normalizeStockMovementsCollection, setStockMovs]);
  const canCreate = canCreateInModule(session,"occurrences") || canEditInModule(session,"occurrences");
  const canEdit = canEditInModule(session,"occurrences");
  const canDelete = canDeleteInModule(session,"occurrences");
  const allowedCostCenters = React.useMemo(()=> bridgeScoped ? [bridgeCostCenter] : getAllowedCostCentersForModule(session, "occurrences"), [bridgeScoped, bridgeCostCenter, session]);
  const canManage  = canEdit;
  const activeProviders = providerDirectory.filter(p=>p.active !== false);
  const moduleCardsConfig = (()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  })();
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.occurrences;
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig]);
  const allowedOccurrenceCards = React.useMemo(()=>getConfiguredSectionCards("moduleHome", ["abertas","em_execucao","aguardando_decisao","concluidas"]), [getConfiguredSectionCards]);
  const filterConfiguredCards = React.useCallback((items=[], allowedIds=[], fallbackIds=[])=>{
    const normalizedAllowed = Array.isArray(allowedIds) ? allowedIds.map(v=>String(v||"")) : [];
    const normalizedFallback = Array.isArray(fallbackIds) ? fallbackIds.map(v=>String(v||"")) : [];
    const normalizedItems = (Array.isArray(items) ? items : []).map((item, index)=>({
      ...item,
      id: String(item?.id || normalizedFallback[index] || `card_${index}`),
    }));
    if(!normalizedAllowed.length) return [];
    return normalizedItems.filter(item=>normalizedAllowed.includes(item.id));
  }, []);
  const enrichedStockItems = enrichStockItemsWithQty(stockItems, stockMovs);
  const normalizeParticipantName = React.useCallback((value) => String(typeof value === "string" ? value : (value?.name || value || "")).trim().toLowerCase(), []);
  const currentEmployee = React.useMemo(()=>{
    const sessionId = String(session?.id || "").trim();
    const sessionUserId = String(session?.userId || "").trim();
    const sessionUsername = String(session?.username || "").trim().toLowerCase();
    const sessionName = normalizeParticipantName(session?.name || myName || "");
    return (employees||[]).find(emp=>{
      const linked = String(emp?.linkedAccountId || emp?.accountId || "").trim();
      const userId = String(emp?.userId || "").trim();
      const username = String(emp?.username || "").trim().toLowerCase();
      const name = normalizeParticipantName(emp?.name || "");
      return (sessionId && linked===sessionId) || (sessionUserId && userId===sessionUserId) || (sessionUsername && username===sessionUsername) || (sessionName && name===sessionName);
    }) || null;
  }, [employees, session?.id, session?.userId, session?.username, session?.name, myName, normalizeParticipantName]);
  const currentIdentity = React.useMemo(()=>({
    accountId:String(session?.id||"").trim(),
    userId:String(session?.userId||"").trim(),
    employeeId:String(currentEmployee?.id||"").trim(),
    linkedAccountId:String(currentEmployee?.linkedAccountId || currentEmployee?.accountId || "").trim(),
    username:String(session?.username || currentEmployee?.username || "").trim().toLowerCase(),
    name:normalizeParticipantName(session?.name || currentEmployee?.name || myName || ""),
  }), [currentEmployee, session?.id, session?.userId, session?.username, session?.name, myName, normalizeParticipantName]);
  const participantMatchesMe = React.useCallback((value)=>{
    if(!value) return false;
    const obj = (value && typeof value === "object") ? value : { name: value };
    const ids = [obj?.id, obj?.employeeId, obj?.userId, obj?.linkedAccountId, obj?.accountId].map(v=>String(v||"").trim()).filter(Boolean);
    if(ids.some(v=> v===currentIdentity.employeeId || v===currentIdentity.accountId || v===currentIdentity.userId || v===currentIdentity.linkedAccountId)) return true;
    const username = String(obj?.username||"").trim().toLowerCase();
    if(username && username===currentIdentity.username) return true;
    const names = [obj?.name, obj?.displayName, obj].map(normalizeParticipantName).filter(Boolean);
    return names.includes(currentIdentity.name);
  }, [currentIdentity, normalizeParticipantName]);
  const formatParticipantLabel = React.useCallback((value)=>{
    if(!value) return "";
    if(typeof value === "string") return value;
    return String(value?.name || value?.displayName || value?.username || value?.employeeId || value?.userId || value?.linkedAccountId || value?.id || "").trim();
  }, []);
  const getOccurrenceParticipants = (occ) => {
    const raw = Array.isArray(occ?.assignees) ? occ.assignees : [];
    return raw.filter(Boolean);
  };
  const isOccurrenceParticipant = (occ) => {
    if(!occ) return false;
    const participants = getOccurrenceParticipants(occ);
    return participantMatchesMe({ id:occ?.assignedToId, employeeId:occ?.assignedToId, name:occ?.assignedTo, username:occ?.assignedToUsername }) || participants.some(participantMatchesMe);
  };
  const canInteractOccurrence = (occ) => canManage || isOccurrenceParticipant(occ);
  const isOccurrenceDeleted = React.useCallback((occ) => isOperationalRecordDeleted(occ), []);
  const visibleOccurrences = occurrences
    .filter(o=>!isOccurrenceDeleted(o) && matchesBridgeCostCenter(o))
    .filter(o=>safeItemMatchesCostCenterAccess(session, "occurrences", o));
  const getOccurrencePhotoEntries = React.useCallback((occ) => normalizePhotoEntries(Array.isArray(occ?.occurrencePhotos) ? occ.occurrencePhotos : (Array.isArray(occ?.photos) ? occ.photos : [])), []);
  const closeOccurrenceForm = React.useCallback(() => {
    const returnId = form?._returnToDetailId || (!form?._new ? form?.id : null);
    setForm(null);
    if (returnId) {
      const fresh = visibleOccurrences.find(o=>String(o.id)===String(returnId)) || occurrences.find(o=>String(o.id)===String(returnId));
      if (fresh) setDetailOcc(fresh);
    }
  }, [form, visibleOccurrences, occurrences]);

  const OccurrenceInlinePanel = React.useCallback(function OccurrenceInlinePanel({ title, onClose, children }) {
    return (
      <div style={{marginTop:14, background:T.card, border:`1px solid ${T.border}`, borderRadius:14, padding:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,marginBottom:12}}>
          <div style={{fontSize:14,fontWeight:800,color:T.text}}>{title}</div>
          <Btn size="sm" variant="ghost" onClick={onClose}>Fechar</Btn>
        </div>
        {children}
      </div>
    );
  }, []);

  const OccurrencePhotosInline = React.useCallback(function OccurrencePhotosInline({ occId, onClose }) {
    const occ = visibleOccurrences.find(o=>String(o.id)===String(occId)) || occurrences.find(o=>String(o.id)===String(occId)) || detailOcc;
    const photos = getOccurrencePhotoEntries(occ);
    const addFiles = async (files) => {
      const entries = await Promise.all(Array.from(files || []).map(file => new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = ev => resolve({ id:createClientId('oph'), title:'Foto', photo:ev.target?.result || '', url:ev.target?.result || '', createdAt:new Date().toISOString(), by:myName });
        reader.readAsDataURL(file);
      })));
      const valid = entries.filter(e => e.photo || e.url);
      if (!valid.length) return;
      const _ts = new Date().toISOString();
      let updatedOccurrence = null;
      applyOccurrences(prev => prev.map(o => {
        if (String(o.id)!==String(occId)) return o;
        updatedOccurrence = markOperationalUpdatePending({
          ...o,
          occurrencePhotos:[...(Array.isArray(o.occurrencePhotos)?o.occurrencePhotos:[]), ...valid.map(entry=>({ ...entry, clientId: entry.clientId || entry.id || createClientId('oph') }))],
          timeline:[...(o.timeline||[]), makeOccurrenceTimelineEntry({ action:'Fotos adicionadas', note:`${valid.length} foto(s)` }, _ts)],
          updatedAt:_ts,
          updatedBy:myName,
        }, { userName: myName, at:_ts, operationType:"occurrence_photos_update" });
        return updatedOccurrence;
      }));
      if (updatedOccurrence && detailOcc?.id===occId) setDetailOcc(updatedOccurrence);
      syncOnSave('occurrences');
      show('✅ Fotos adicionadas.');
    };
    const removePhoto = (idx) => {
      const _ts = new Date().toISOString();
      let updatedOccurrence = null;
      applyOccurrences(prev => prev.map(o => {
        if (String(o.id)!==String(occId)) return o;
        const nextPhotos = getOccurrencePhotoEntries(o).filter((_,i)=>i!==idx);
        updatedOccurrence = markOperationalUpdatePending({ ...o, occurrencePhotos: nextPhotos, timeline:[...(o.timeline||[]), makeOccurrenceTimelineEntry({ action:'Foto removida', note:'' }, _ts)], updatedAt:_ts, updatedBy:myName }, { userName: myName, at:_ts, operationType:"occurrence_photos_update" });
        return updatedOccurrence;
      }));
      if (updatedOccurrence && detailOcc?.id===occId) setDetailOcc(updatedOccurrence);
      syncOnSave('occurrences');
    };
    return (
      <OccurrenceInlinePanel title="📷 Fotos da Ocorrência" onClose={onClose}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <input type="file" accept="image/*" multiple onChange={e=>{ addFiles(e.target.files); e.target.value=''; }} style={{fontSize:12,color:T.muted,width:'100%'}} />
          {photos.length>0 ? (
            <div style={{display:'grid',gridTemplateColumns:isCompactViewport()?'1fr 1fr':'repeat(auto-fill,minmax(140px,1fr))',gap:12}}>
              {photos.map((ph, idx)=>(
                <div key={ph.id || idx} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:10,display:'flex',flexDirection:'column',gap:8}}>
                  <img src={ph.photo||ph.url||ph.thumb} alt="" style={{width:'100%',height:120,objectFit:'cover',borderRadius:10,cursor:'zoom-in'}} onClick={()=>setOccPhotoLightbox({ photos, idx })} />
                  <div style={{fontSize:12,color:T.muted}}>{ph.title || `Foto ${idx+1}`}</div>
                  <button type="button" onClick={()=>removePhoto(idx)} style={{alignSelf:'stretch',background:`${T.red}18`,border:`1px solid ${T.red}33`,color:T.red,cursor:'pointer',fontSize:13,fontWeight:700,borderRadius:10,padding:'10px 12px'}}>Excluir foto</button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{fontSize:12,color:T.muted}}>Nenhuma foto adicionada ainda.</div>
          )}
        </div>
      </OccurrenceInlinePanel>
    );
  }, [visibleOccurrences, occurrences, detailOcc, getOccurrencePhotoEntries, myName, applyOccurrences, syncOnSave, show, setDetailOcc, makeOccurrenceTimelineEntry]);

  const getOccurrencePhotoBatches = (occ) => {
    const entries = getOccurrencePhotoEntries(occ);
    const grouped = new Map();
    entries.forEach((entry, idx) => {
      const key = `${entry.addedAt || entry.ts || 'sem-ts'}__${entry.addedBy || entry.by || 'Usuário'}`;
      if(!grouped.has(key)) grouped.set(key, { key, addedAt: entry.addedAt || entry.ts || '', addedBy: entry.addedBy || entry.by || '', photos: [] });
      grouped.get(key).photos.push(entry);
    });
    return Array.from(grouped.values()).sort((a,b)=>(new Date(b.addedAt||0).getTime())-(new Date(a.addedAt||0).getTime()));
  };
  const canAddPhotosToOccurrence = (occ) => {
    if(!occ) return false;
    if(!occ.completionDate && !occ.completedAt && occ.status !== 'Concluída') return true;
    const base = occ.completedAt || occ.completionDate;
    if(!base) return true;
    const diff = Date.now() - new Date(base).getTime();
    return diff <= 7 * 24 * 60 * 60 * 1000;
  };
  useEffect(()=>{
    const normalized = normalizeOccurrencesCollection(occurrences);
    if(JSON.stringify(normalized)!==JSON.stringify(occurrences)){
      setOccurrences(normalized);
    }
  }, [normalizeOccurrencesCollection, occurrences, setOccurrences]);

  useEffect(()=>{
    const normalized = normalizeStockMovementsCollection(stockMovs);
    if(JSON.stringify(normalized)!==JSON.stringify(stockMovs)){
      setStockMovs(normalized);
    }
  }, [normalizeStockMovementsCollection, stockMovs, setStockMovs]);

  useEffect(()=>{
    if(!detailOcc?.id) return;
    const fresh = visibleOccurrences.find(o=>o.id===detailOcc.id);
    if(fresh){
      setDetailOcc(fresh);
      setOccSchedDate(prev => fresh.scheduledDate || prev || today());
    }
  }, [visibleOccurrences, detailOcc?.id]);

  useEffect(()=>{
    if(!openOccurrenceId) return;
    const targetId = String(openOccurrenceId || "").trim();
    if(!targetId || consumedOpenOccurrenceIdRef.current === targetId) return;

    // v3.6.01: hybrid guard — mesmo padrão de orders (v3.4.09) e services (v3.5.01).
    // Não dispara requestPrioritySync imediatamente ao receber openOccurrenceId.
    // CAMINHO A (Live hidratou via fz:live-occurrence-hydrated): tenta abrir após
    // 170ms, depois agenda requestPrioritySync com 600ms de delay, garantindo que
    // o live cache já tem o UDT antes do pull_delta chegar.
    // CAMINHO B (pull_delta hidratou via fz:bg:updated): tryOpen roda após sync.
    // CAMINHO C (registro já na lista): abre imediatamente, sem sync extra.
    let liveHydratedThis = false;
    let prioritySyncScheduled = false;

    const matchesTarget = (o) => String(o?.id || "") === targetId || String(o?.clientId || "") === targetId || String(o?.recordId || "") === targetId;
    const tryOpen = () => {
      const match = visibleOccurrences.find(matchesTarget) || occurrences.find(matchesTarget);
      if(match){
        setDetailOcc(match);
        setOccSchedDate(match.scheduledDate || today());
        consumedOpenOccurrenceIdRef.current = targetId;
        if(onClearOpenOccurrenceRef.current) onClearOpenOccurrenceRef.current();
        return true;
      }
      return false;
    };

    const schedulePSync = (reason, delayMs = 0) => {
      if(prioritySyncScheduled) return;
      if(!currentFarmId || !syncCtx?.requestPrioritySync) return;
      prioritySyncScheduled = true;
      setTimeout(()=>{
        syncCtx.requestPrioritySync({
          collection: "occurrences",
          farmId: currentFarmId,
          recordId: openOccurrenceId,
          reason,
        }).catch(()=>{});
      }, delayMs);
    };

    // Tentar abrir imediatamente se já está na lista.
    tryOpen();

    // Caminho B: pull_delta/bg updated hidratou.
    const onDone = (event) => {
      const detail = event?.detail || {};
      if(detail?.collection && detail.collection !== "occurrences") return;
      if(liveHydratedThis) return;
      tryOpen();
    };

    // Caminho A: Live hidratou → tentar abrir após 170ms → sync com 600ms.
    const onLiveHydrated = (event) => {
      const detail = event?.detail || {};
      const evtId = String(detail?.recordId || detail?.record?.id || detail?.record?.clientId || "").trim();
      if(!evtId || evtId !== targetId) return;
      liveHydratedThis = true;
      setTimeout(()=>{ tryOpen(); }, 170);
      schedulePSync("live_hydrated_post_open", 600);
    };

    // Fallback: se nenhum evento chegou em 4500ms.
    const timer = setTimeout(()=>{
      if(!tryOpen() && !liveHydratedThis) {
        schedulePSync("record_not_found_local", 0);
      }
    }, 4500);

    window.addEventListener("fz:bg:updated", onDone);
    window.addEventListener("fz:priority-sync-done", onDone);
    window.addEventListener("fz:live-occurrence-hydrated", onLiveHydrated);
    return ()=>{
      window.removeEventListener("fz:bg:updated", onDone);
      window.removeEventListener("fz:priority-sync-done", onDone);
      window.removeEventListener("fz:live-occurrence-hydrated", onLiveHydrated);
      clearTimeout(timer);
    };
  }, [openOccurrenceId, visibleOccurrences, occurrences, currentFarmId, syncCtx]);

  // v3.6.02: fallback pós-ACK de Live para occurrences.
  // Equivalente ao onServicesHydratedAfterAck de services (v3.5.04/05).
  // Live local é bloqueada enquanto há PRV/UDT (correto). Após ACK oficial,
  // este listener tenta disparar a Live com o número confirmado do servidor.
  React.useEffect(() => {
    const onOccurrencesHydratedAfterAck = (event) => {
      const detail = event?.detail || {};
      if (detail?.collection && detail.collection !== "occurrences") return;
      if (detail?.source !== "operational-ack" && detail?.source !== "live-occurrence-hydrate") return;
      const fid = String(detail?.farmId || currentFarmId || "").trim();
      if (!fid) return;
      setTimeout(() => {
        (async () => {
          try {
            const localKey = `fz:${fid}:occurrences`;
            const liveList = getLiveCollectionCache(localKey);
            const dbList = await DB.get(localKey).catch(() => []);
            const stateList = Array.isArray(occurrences) ? occurrences : [];
            const mergedById = new Map();
            const addList = (arr) => {
              if (!Array.isArray(arr)) return;
              arr.forEach((occ) => {
                if (!occ || typeof occ !== "object") return;
                const id = String(occ?.id || occ?.clientId || occ?.recordId || occ?.recordNumber || Math.random()).trim();
                mergedById.set(id, { ...(mergedById.get(id) || {}), ...occ });
              });
            };
            addList(stateList);
            addList(dbList);
            addList(liveList);
            const list = Array.from(mergedById.values());
            const ackIds = new Set((Array.isArray(detail?.acknowledgedOperations) ? detail.acknowledgedOperations : [])
              .flatMap((ack) => [ack?.id, ack?.clientId, ack?.recordId, ack?.operationId, ack?.pendingOperationId, ack?.serverAckOperationId, ack?.ackedOperationId])
              .map((v) => String(v || "").trim())
              .filter(Boolean));
            const candidates = list.filter((occ) => {
              if (!occ) return false;
              if (String(occ?.farmId || fid || "").trim() !== fid) return false;
              if (ackIds.size === 0) return true;
              const ids = [occ?.id, occ?.clientId, occ?.recordId, occ?.pendingOperationId, occ?.localOperationId, occ?.serverAckOperationId, occ?.ackedOperationId]
                .map((v) => String(v || "").trim())
                .filter(Boolean);
              return ids.some((id) => ackIds.has(id));
            });
            const official = candidates.filter((occ) => {
              const rn = String(occ?.recordNumber || "").trim();
              if (!rn || rn.toUpperCase().endsWith("-PRV")) return false;
              if (occ?.pendingLocalCreate || occ?.pendingLocalUpdate || occ?.pendingLocalDelete || occ?.pendingLocalChange || occ?.updatePending) return false;
              if (occ?._deleted || occ?.deletedAt || occ?.deleted_at) return false;
              return !!occ?.serverConfirmed || !!occ?.serverAckAt || String(occ?.syncStatus || "").toLowerCase() === "synced";
            });
            const recent = official.sort((a, b) => String(b?.serverAckAt || b?.updatedAt || b?.createdAt || "").localeCompare(String(a?.serverAckAt || a?.updatedAt || a?.createdAt || "")))[0];
            if (!recent) return;
            const recId = String(recent?.id || recent?.clientId || recent?.recordId || "").trim();
            const rn = String(recent?.recordNumber || "").trim();
            const key = `${fid}:${recId}:${rn}`;
            const last = Number(occurrenceLiveAckFallbackRef.current[key] || 0);
            if (last && Date.now() - last < 45000) return;
            occurrenceLiveAckFallbackRef.current = { ...occurrenceLiveAckFallbackRef.current, [key]: Date.now() };
            appendOccurrenceNotification(recent, {
              event: "occurrence_created_ack_official",
              eventLabel: "Ocorrência confirmada",
              title: "Ocorrência confirmada",
              summary: `Ocorrência ${rn || recId} confirmada pelo servidor.`,
              status: recent?.status || "Aberta",
              entityLabel: "a ocorrência",
              eventAt: recent?.serverAckAt || recent?.updatedAt || new Date().toISOString(),
            });
          } catch (_err) {}
        })();
      }, 900);
    };
    window.addEventListener("fz:occurrences:hydrated", onOccurrencesHydratedAfterAck);
    return () => window.removeEventListener("fz:occurrences:hydrated", onOccurrencesHydratedAfterAck);
  }, [appendOccurrenceNotification, currentFarmId, occurrences]);


  const defForm = {
    _new:true, title:"", category:"", description:"", location:"", poiId:"", machineId:"", costCenter:bridgeCostCenter || allowedCostCenters[0] || "Sede", costCenters: bridgeCostCenter ? [bridgeCostCenter] : (allowedCostCenters[0] ? [allowedCostCenters[0]] : []),
    urgency:"Normal", status:"Aberta", execType:"",
    providerId:"", assignedTo:"",
    budget:{ value:0, date:today(), notes:"", photos:[] },
    scheduledDate:today(), completionDate:"",
    notes:"", photos:[], occurrencePhotos:[], timeline:[],
    reportedBy: myName, reportedAt: new Date().toISOString(),
  };


  function save() {
    if(form?._new && !canCreate) { show("Sem permissão para criar ocorrências.","error"); return; }
    if(!form?._new && !canEdit)  { show("Sem permissão para editar ocorrências.","error"); return; }
    const draftCenters = itemCostCenters(form, form?.costCenter);
    if(draftCenters.some(cc=>!canViewCostCenter(session,"occurrences",cc))){ show("Sem acesso a um ou mais centros de custo desta ocorrência.","error"); return; }
    if(draftCenters.some(cc=>!canEditCostCenter(session,"occurrences",cc))){ show("Sem permissão para editar um ou mais centros de custo desta ocorrência.","error"); return; }
    if(!form.title.trim())  { show("Título obrigatório.","error"); return; }
    if(!form.category)      { show("Selecione a categoria.","error"); return; }
    if(!String(form.costCenter || "").trim()) { show("Selecione o centro de custo da ocorrência.","error"); return; }
    const{_new,...d}=form;
    const _ts = new Date().toISOString();
    if(_new){
      const maintenancePatch = buildOccurrenceMaintenancePatch(d, { status: d.status || "Aberta", startedAt: d.startedAt, completedAt: d.completedAt, completionDate: d.completionDate });
      const occClientId = createClientId("occ");
      const occurrenceCostCenter = d.costCenter || bridgeCostCenter || allowedCostCenters[0] || "Sede";
      const provisionalNumber = makeProvisionalOccurrenceNumber(occurrences, occurrenceCostCenter);
      const newOcc = markOperationalCreatePending(stampRecordGeneration({...d, costCenter:occurrenceCostCenter, costCenters:(Array.isArray(d.costCenters)&&d.costCenters.length?d.costCenters:[occurrenceCostCenter]).filter(Boolean), id:occClientId, clientId:occClientId,
        ...provisionalNumber,
        ...maintenancePatch,
        timeline:[makeOccurrenceTimelineEntry({action:"🔴 Ocorrência registrada",note:d.description}, _ts)],
         ...makeCreateMeta(session?.name || "")}, currentFarm), { userName: session?.name || myName, at:_ts });
      applyOccurrences(prev=>[...prev, newOcc]);
      appendOccurrenceNotification(newOcc, {
        event:"occurrence_created",
        eventLabel:"Ocorrência registrada",
        title:"Nova ocorrência registrada",
        summary:`${myName || newOcc?.reportedBy || "Sistema"} registrou a ocorrência ${newOcc?.recordNumber || newOcc?.id || ""}${newOcc?.costCenter ? ` · ${newOcc.costCenter}` : ""}.`,
        body:newOcc?.description || "Nova ocorrência registrada.",
        status:newOcc?.status || "Aberta",
        eventAt:_ts,
      });
    } else {
      const prev = visibleOccurrences.find(o=>o.id===d.id);
      let tl = d.timeline||[];
      if(prev?.status!==d.status) tl=[...tl,makeOccurrenceTimelineEntry({action:`Status: ${prev.status} → ${d.status}`,note:""}, _ts)];
      let nextOcc = null;
      applyOccurrences(prev=>prev.map(o=>{
        if(o.id!==d.id) return o;
        const maintenancePatch = buildOccurrenceMaintenancePatch(o, { ...d, status: d.status, startedAt: d.startedAt, completedAt: d.completedAt, completionDate: d.completionDate });
        nextOcc = markOperationalUpdatePending(stampRecordGeneration({...o,...d,id:String(d.id || o.id),clientId:String(d.clientId || o.clientId || d.id || o.id),costCenter:d.costCenter || bridgeCostCenter || o.costCenter || allowedCostCenters[0] || "Sede", costCenters:(Array.isArray(d.costCenters)&&d.costCenters.length?d.costCenters:itemCostCenters(o, d.costCenter || bridgeCostCenter || o.costCenter || allowedCostCenters[0] || "Sede")).filter(Boolean), ...maintenancePatch,timeline:tl, ...makeUpdateMeta(session?.name || "")}, currentFarm), { userName: session?.name || myName, at:_ts, operationType:"occurrence_update" });
        return nextOcc;
      }));
      if(prev && nextOcc){ notifyOccurrenceStatusChange(prev, nextOcc, { eventAt:_ts }); }
    }
    const returnId = form?._returnToDetailId;
    show("✅ Salvo!");
    setForm(null);
    if(returnId){
      setTimeout(()=>{
        const fresh = visibleOccurrences.find(o=>String(o.id)===String(returnId)) || occurrences.find(o=>String(o.id)===String(returnId));
        if(fresh) setDetailOcc(fresh);
      }, 0);
    }
    syncOnSave("occurrences");
  }

  function updateOccurrence(occId, patch, action, note="") {
    const _ts = new Date().toISOString();
    let updatedOcc = null;
    const previousOcc = visibleOccurrences.find(o=>o.id===occId) || occurrences.find(o=>o.id===occId) || null;
    applyOccurrences(prev => prev.map(o=>{
      if(o.id!==occId) return o;
      const timeline = [...(o.timeline||[]), ...(action?[makeOccurrenceTimelineEntry({action,note}, _ts)]:[])];
      const maintenancePatch = buildOccurrenceMaintenancePatch(o, patch||{});
      updatedOcc = markOperationalUpdatePending({...o, ...patch, ...maintenancePatch, timeline, ...makeUpdateMeta(myName)}, { userName: myName, at:_ts, operationType:"occurrence_update" });
      return updatedOcc;
    }));
    if(updatedOcc && previousOcc){ notifyOccurrenceStatusChange(previousOcc, updatedOcc, { eventAt:_ts }); }
    if(updatedOcc && detailOcc?.id===occId) setDetailOcc(updatedOcc);
    if(updatedOcc && form?.id===occId) setForm(updatedOcc);
    syncOnSave("occurrences");
  }

  function saveOccurrenceMaterials(occId, items) {
    const valid = (items||[]).map(i=>({
      ...i,
      qtySeparated:Number(i.qtySeparated||0),
      qtyUsed:(i.qtyUsed===undefined || i.qtyUsed===null || String(i.qtyUsed).trim()==="") ? null : Number(i.qtyUsed),
    })).filter(i=>i.stockId && i.qtySeparated>0);
    if(!valid.length){ show("Adicione ao menos 1 material.","error"); return; }
    const _ts = new Date().toISOString();
    const currentOcc = visibleOccurrences.find(o=>o.id===occId) || occurrences.find(o=>o.id===occId);
    const newMovs = [];
    const materials = [];
    for (const i of valid) {
      const st = enrichedStockItems.find(s=>String(s.id)===String(i.stockId));
      if(!st) continue;
      if(i.qtyUsed!=null && i.qtyUsed > i.qtySeparated){ show(`A quantidade utilizada não pode ser maior que a separada em ${st.name}.`,"error"); return; }
      if(Number(st.quantity||0) < i.qtySeparated){ show(`Estoque insuficiente para ${st.name}. Saldo: ${st.quantity||0} ${st.unit||""}`,"error"); return; }
      const materialClientId = createClientId("omat");
      newMovs.push(makeOccurrenceStockMovementEntry({
        itemId:i.stockId, type:"saida", qty:i.qtySeparated,
        notes:`Ocorrência: ${currentOcc?.title||("#"+occId)} · material separado`, by:myName,
        costCenter:st.costCenter||"Sede", ts:_ts, updatedAt:_ts,
        origin:"occurrence_material", originId:occId, originMaterialClientId:materialClientId, farmId: currentFarmId || currentOcc?.farmId || "",
      }, _ts));
      if(i.qtyUsed!=null && i.qtySeparated > i.qtyUsed){
        newMovs.push(makeOccurrenceStockMovementEntry({
          itemId:i.stockId, type:"entrada", qty:(i.qtySeparated-i.qtyUsed),
          notes:`Ocorrência: ${currentOcc?.title||("#"+occId)} · devolução de material`, by:myName,
          costCenter:st.costCenter||"Sede", ts:_ts, updatedAt:_ts,
          origin:"occurrence_material_return", originId:occId, originMaterialClientId:materialClientId, farmId: currentFarmId || currentOcc?.farmId || "",
        }, _ts));
      }
      materials.push(makeOccurrenceMaterialEntry({
        id:materialClientId, clientId:materialClientId, stockId:i.stockId, name:st.name, unit:st.unit||"un",
        qtySeparated:i.qtySeparated, qtyUsed:i.qtyUsed, qty:(i.qtyUsed==null?i.qtySeparated:i.qtyUsed), pendingUsage:i.qtyUsed==null,
        by:myName, ts:_ts,
      }, _ts));
    }
    if(!materials.length){ show("Nenhum material válido selecionado.","error"); return; }
    const historyNote = materials.map(m=>`${m.name}: sep. ${m.qtySeparated} / util. ${m.pendingUsage?"pendente":m.qtyUsed} ${m.unit}`).join(" · ");
    const historyEntry = makeOccurrenceTimelineEntry({ action:"Materiais adicionados", note:historyNote }, _ts);
    let updatedOccurrence = null;
    applyOccurrences(prev => prev.map(o => {
      if(o.id !== occId) return o;
      updatedOccurrence = markOperationalUpdatePending({
        ...o,
        materials:[...(o.materials||[]), ...materials],
        timeline:[...(o.timeline||[]), historyEntry],
        updatedAt:_ts,
        updatedBy:myName,
      }, { userName: myName, at:_ts, operationType:"occurrence_materials_update" });
      return updatedOccurrence;
    }));
    if(updatedOccurrence && detailOcc?.id===occId) setDetailOcc(updatedOccurrence);
    if(updatedOccurrence && form?.id===occId) setForm(updatedOccurrence);
    setStockMovs(prev => [...prev, ...newMovs]);
    syncOnSave("occurrences");
    syncOnSave("stockMovements");
    show(materials.some(m=>m.pendingUsage)?"✅ Materiais separados. Depois informe a quantidade utilizada; se não informar, valerá a quantidade separada.":"✅ Materiais adicionados com sucesso!");
    setOccMatForm(null);
  }

  function saveOccurrenceMaterialUsage(occId, materialIndex, qtyUsedValue) {
    const qtyUsed = Number(qtyUsedValue);
    if(!Number.isFinite(qtyUsed) || qtyUsed < 0){ show("Informe a quantidade utilizada.","error"); return; }
    const occ = occurrences.find(o=>o.id===occId) || detailOcc;
    const material = occ?.materials?.[materialIndex];
    if(!occ || !material){ show("Material não encontrado.","error"); return; }
    const qtySeparated = Number(material.qtySeparated || material.qty || 0);
    if(qtyUsed > qtySeparated){ show("A quantidade utilizada não pode ser maior que a separada.","error"); return; }
    const previouslyAccounted = Number(material.qtyUsed ?? material.qty ?? qtySeparated);
    const st = enrichedStockItems.find(s=>String(s.id)===String(material.stockId));
    const _ts = new Date().toISOString();
    const newMovs = [];
    if(qtyUsed < previouslyAccounted){
      newMovs.push(makeOccurrenceStockMovementEntry({
        itemId:material.stockId, type:"entrada", qty:(previouslyAccounted-qtyUsed),
        notes:`Ocorrência: ${occ?.title||("#"+occId)} · ajuste da quantidade utilizada`, by:myName,
        costCenter:st?.costCenter||occ?.costCenter||"Sede", ts:_ts, updatedAt:_ts,
        origin:"occurrence_material_usage_adjust", originId:occId, originMaterialClientId:material.clientId || material.id || "", farmId: currentFarmId || occ?.farmId || "",
      }, _ts));
    } else if(qtyUsed > previouslyAccounted){
      newMovs.push(makeOccurrenceStockMovementEntry({
        itemId:material.stockId, type:"saida", qty:(qtyUsed-previouslyAccounted),
        notes:`Ocorrência: ${occ?.title||("#"+occId)} · uso adicional de material`, by:myName,
        costCenter:st?.costCenter||occ?.costCenter||"Sede", ts:_ts, updatedAt:_ts,
        origin:"occurrence_material_usage_adjust", originId:occId, originMaterialClientId:material.clientId || material.id || "", farmId: currentFarmId || occ?.farmId || "",
      }, _ts));
    }
    const note = `${material.name||st?.name||material.stockId}: utilizado ${qtyUsed} ${material.unit||st?.unit||"un"}${qtyUsed<qtySeparated?` (retorno ${qtySeparated-qtyUsed})`:""}`;
    const timelineEntry = makeOccurrenceTimelineEntry({ action:"Quantidade utilizada informada", note }, _ts);
    let updatedOccurrence = null;
    applyOccurrences(prev=>prev.map(o=>{
      if(o.id!==occId) return o;
      const nextMaterials = (o.materials||[]).map((m,idx)=> idx!==materialIndex ? m : makeOccurrenceMaterialEntry({ ...m, qtyUsed, qty:qtyUsed, pendingUsage:false, usedUpdatedAt:_ts, usedUpdatedBy:myName }, _ts));
      updatedOccurrence = markOperationalUpdatePending({ ...o, materials:nextMaterials, timeline:[...(o.timeline||[]), timelineEntry], updatedAt:_ts, updatedBy:myName }, { userName: myName, at:_ts, operationType:"occurrence_material_usage" });
      return updatedOccurrence;
    }));
    if(updatedOccurrence && detailOcc?.id===occId) setDetailOcc(updatedOccurrence);
    if(updatedOccurrence && form?.id===occId) setForm(updatedOccurrence);
    if(newMovs.length){ setStockMovs(prev=>[...prev, ...newMovs]); syncOnSave("stockMovements"); }
    syncOnSave("occurrences");
    setOccMaterialUsageDrafts(prev=>{ const next={...prev}; delete next[`${occId}::${materialIndex}`]; return next; });
    show("✅ Quantidade utilizada atualizada.");
  }
  function confirmOccurrenceMaterialRestock(occ, note="") {
    const latestOcc = occurrences.find(o=>o.id===occ?.id) || occ;
    if(hasCompletedMaterialRestock(latestOcc)){
      setOccRestockForm(null);
      show("Esta ocorrência já teve os materiais estornados.","info");
      return;
    }
    const materials = getRestockableMaterials(latestOcc);
    if(!materials.length){
      show("Esta ocorrência não possui materiais para estorno.","info");
      setOccRestockForm(null);
      return;
    }
    const _ts = new Date().toISOString();
    const movements = materials.map((m, idx)=>makeOccurrenceStockMovementEntry({
      itemId:m.stockId,
      type:"entrada",
      qty:getMaterialUsageQty(m),
      notes:`Ocorrência: ${latestOcc?.title||("#"+latestOcc?.id)} · estorno para exclusão${note?` · ${note}`:""}`,
      by:myName,
      costCenter:latestOcc?.costCenter || m?.costCenter || "Sede",
      ts:_ts,
      updatedAt:_ts,
      origin:"occurrence_delete_restock",
      originId:latestOcc?.id,
      originMaterialClientId:m?.clientId || m?.id || "",
      farmId: currentFarmId || latestOcc?.farmId || "",
    }, _ts));
    applyStockMovements(prev=>[...prev, ...movements]);
    updateOccurrence(latestOcc.id, {
      materialRestockCompletedAt:_ts,
      materialRestockCompletedBy:myName,
      materialRestockMovementIds:movements.map(m=>m.id),
    }, "♻️ Estorno de materiais", note || "Materiais devolvidos ao estoque para habilitar exclusão");
    syncOnSave("stockMovements");
    setOccRestockForm(null);
    show("✅ Estorno realizado. Agora a exclusão da ocorrência está habilitada.");
  }
  function requestOccurrenceDelete(occ) {
    const latestOcc = occurrences.find(o=>o.id===occ?.id) || occ;
    if(hasPendingMaterialRestock(latestOcc)){
      setOccRestockForm(latestOcc);
      return;
    }
    const _ts=new Date().toISOString();
    applyOccurrences(p=>p.map(o=>o.id!==latestOcc.id?o:markOperationalUpdatePending({...o,_deleted:true,deletedAt:_ts,deleted_at:_ts,deletedBy:myName,status:"Cancelada",updatedAt:_ts,updatedBy:myName,timeline:[...(o.timeline||[]),makeOccurrenceTimelineEntry({action:"🗑️ Ocorrência excluída",note:"Registro removido"}, _ts)]}, { userName: myName, at:_ts, operationType:"occurrence_delete" })));
    if(detailOcc?.id===latestOcc.id) setDetailOcc(null);
    setForm(null);
    show("🗑️ Excluída.");
    syncOnSave("occurrences");
  }

  const occurrenceLastActivityTime = React.useCallback((record)=>{
    const candidates = [record?.updatedAt, record?.updated_at, record?.modifiedAt, record?.serverAckAt, record?.createdAt, record?.created_at, record?.reportedAt, record?.date];
    for(const value of candidates){
      const ts = Date.parse(String(value || ""));
      if(Number.isFinite(ts)) return ts;
    }
    return 0;
  }, []);
  const compareOccurrencesByLastActivity = React.useCallback((a,b)=>{
    const diff = occurrenceLastActivityTime(b) - occurrenceLastActivityTime(a);
    if(diff) return diff;
    return compareOperationalRecords(a,b);
  }, [occurrenceLastActivityTime]);

  const filtered = visibleOccurrences
    .filter(o=>{
      const status = String(o?.status||"").trim();
      if(cardFilter==="open") return !closedOccurrenceStatuses.has(status);
      if(cardFilter==="running") return status==="Em Execução";
      if(cardFilter==="waiting") return ["Orçamento Recebido","Aguardando Aprovação","Aguardando Decisão","Pausada"].includes(status);
      if(cardFilter==="done") return ["Concluída","Concluído"].includes(status);
      return true;
    })
    .filter(o=>filterStatus==="Todos"||o.status===filterStatus)
    .filter(o=>filterCat==="Todos"||o.category===filterCat)
    .sort(compareOccurrencesByLastActivity);

  return(
    <div><Toast toast={toast}/>


      {!detailOcc && !form && <>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <h2 style={{margin:0,color:T.text,fontSize:20,fontWeight:800}}>🚨 Ocorrências</h2>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>{visibleOccurrences.filter(o=>!closedOccurrenceStatuses.has(String(o?.status||"").trim())).length} ocorrências em aberto</div>
        </div>
        {canCreate&&<Btn onClick={()=>setForm({...defForm})}>+ Nova Ocorrência</Btn>}
      </div>

      {/* Stats */}
      {(()=>{
        const cards = filterConfiguredCards([
          { id:"abertas", node:<StatCard icon="🔴" label="Ocorrências Abertas" value={visibleOccurrences.filter(o=>!closedOccurrenceStatuses.has(String(o?.status||"").trim())).length} color={T.red} onClick={()=>{ setCardFilter("open"); setFilterStatus("Todos"); }}/> },
          { id:"em_execucao", node:<StatCard icon="⚡" label="Em Execução" value={visibleOccurrences.filter(o=>o.status==="Em Execução").length} color={T.yellow} onClick={()=>{ setCardFilter("running"); setFilterStatus("Todos"); }}/> },
          { id:"aguardando_decisao", node:<StatCard icon="⏳" label="Aguardando Decisão" value={visibleOccurrences.filter(o=>["Orçamento Recebido","Aguardando Aprovação","Aguardando Decisão","Pausada"].includes(o.status)).length} color={T.accent} onClick={()=>{ setCardFilter("waiting"); setFilterStatus("Todos"); }}/> },
          { id:"concluidas", node:<StatCard icon="✅" label="Concluídas" value={visibleOccurrences.filter(o=>["Concluída","Concluído"].includes(String(o?.status||"").trim())).length} color={T.green} onClick={()=>{ setCardFilter("done"); setFilterStatus("Todos"); }}/> },
        ], allowedOccurrenceCards, ["abertas","em_execucao","aguardando_decisao","concluidas"]);
        return cards.length ? <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:12,marginBottom:16}}>{cards.map(item=><React.Fragment key={item.id}>{item.node}</React.Fragment>)}</div> : null;
      })()}

      {/* Filters */}
      <div style={{marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Filtros</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:8,alignItems:"center"}}>
          <Select value={filterStatus} onChange={e=>{ setCardFilter("all"); setFilterStatus(e.target.value); }} style={{width:"100%",minWidth:0}}>
            <option value="Todos">Status</option>
            {Object.keys(OCCURRENCE_STATUS).map(s=><option key={s}>{s}</option>)}
          </Select>
          <Select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{width:"100%",minWidth:0}}>
            <option value="Todos">Categorias</option>
            {OCCURRENCE_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
          </Select>
        </div>
      </div>

      {/* List */}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {filtered.map(occ=>{
          const cfg = OCCURRENCE_STATUS[occ.status]||{color:T.muted,icon:"📋"};
          const realCat = OCCURRENCE_CATEGORIES.find(c=>c.id===occ.category);
          const prov = providerDirectory.find(p=>p.id===occ.providerId);
          const days = daysSince(occ.createdAt||occ.reportedAt);
          const occPriority = getPriorityConfig("occurrence", occ.priority || (occ.urgency==="Emergência"?"emergency":occ.urgency==="Urgente"?"urgent":occ.urgency==="Alta"?"high":"normal"));
          return(
            <div key={occ.id} onClick={()=>{ setDetailOcc(occ); setOccSchedDate(occ.scheduledDate || today()); }}
              style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"12px 16px",
                cursor:"pointer",transition:"all .15s",borderLeft:`3px solid ${cfg.color||T.red}`,opacity:["Concluída","Cancelada"].includes(occ.status)?.7:1}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
              onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",gap:5,marginBottom:3,flexWrap:"wrap"}}>
                    <Badge color={cfg.color}>{cfg.icon} {occ.status}</Badge>
                    {realCat&&<Badge color={T.muted}>{realCat.icon} {realCat.label}</Badge>}
                    {occPriority&&<Badge color={occPriority.color}>{occPriority.icon} {occPriority.label}</Badge>}
                  </div>
                  <div style={{fontSize:11,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:2}}>{displayOccurrenceRecordNumber(occ)}</div>
                      <div style={{fontWeight:700,color:T.text,fontSize:14}}>{occ.title}{occ.costCenter?` - ${occ.costCenter}`:""}</div>
                  <div style={{fontSize:11,color:T.muted,marginTop:3,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
                    <span>📅 {fmtDate(occ.scheduledDate||occ.createdAt||"")}</span>
                    <span style={{color:days>30?T.red:days>15?T.yellow:T.muted}}>⏱ {days}d</span>
                    {occ.location&&<span>📍 {occ.location}</span>}
                    {prov&&<span>👷 {prov.name}</span>}
                    {occ.assignedTo&&<span>👤 {occ.assignedTo}</span>}
                  </div>
                  {getOccurrenceParticipants(occ).length>0&&<div style={{fontSize:11,color:T.muted,marginTop:6,display:"flex",gap:6,flexWrap:"wrap"}}>{getOccurrenceParticipants(occ).map((participant,idx)=>{ const label=formatParticipantLabel(participant); if(!label) return null; const key=String((participant&&typeof participant==="object")?(participant.id||participant.employeeId||participant.userId||participant.linkedAccountId||participant.username||label):label)+"::"+idx; return <span key={key} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:999,padding:"4px 8px",display:"inline-flex",alignItems:"center",gap:5}}><span style={{color:T.accent}}>👥</span><span>{label}</span></span>; })}</div>}
                  {occ.status==="Pausada" && (occ.pauseReason||occ.pauseNote) && <div style={{fontSize:11,color:T.yellow,marginTop:6,fontWeight:600}}>⏸️ {occ.pauseReason||"Pausada"}{occ.pauseNote?` — ${occ.pauseNote}`:""}</div>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                  <div title={(getOccurrencePhotoEntries(occ).length>0?`${getOccurrencePhotoEntries(occ).length} foto(s) na ocorrência`:`Sem fotos na ocorrência`)} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:999,border:`1px solid ${getOccurrencePhotoEntries(occ).length>0?`${T.accent}44`:T.border}`,background:getOccurrencePhotoEntries(occ).length>0?`${T.accent}14`:T.card2,color:getOccurrencePhotoEntries(occ).length>0?T.accent:T.muted,fontSize:11,fontWeight:700}}><ServicePhotoStatusIcon size={16} hasPhotos={getOccurrencePhotoEntries(occ).length>0}/><span>{getOccurrencePhotoEntries(occ).length>0?getOccurrencePhotoEntries(occ).length:"0"}</span></div>
                  <div style={{fontSize:12,color:T.accent,whiteSpace:"nowrap"}}>Ver →</div>
                  {canEdit&&<Btn size="sm" variant="ghost" onClick={e=>{e.stopPropagation();setForm({...occ,_new:false});}}>✏️</Btn>}
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length===0&&<div style={{color:T.muted,textAlign:"center",padding:48}}>Nenhuma ocorrência encontrada.</div>}
      </div>

      </>}

      {detailOcc&&(()=>{
        const cfg  = OCCURRENCE_STATUS[detailOcc.status]||{color:T.muted,icon:"📋"};
        const cat  = OCCURRENCE_CATEGORIES.find(c=>c.id===detailOcc.category);
        const prov = providerDirectory.find(p=>p.id===detailOcc.providerId);
        const days = daysSince(detailOcc.createdAt||detailOcc.reportedAt);
        return(
          <div>
            <button onClick={()=>{ if(onBridgeBack) onBridgeBack(); else setDetailOcc(null); }} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button>
            <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px",marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,gap:12}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
                    <Badge color={cfg.color}>{cfg.icon} {detailOcc.status}</Badge>
                    {cat&&<Badge color={T.muted}>{cat.icon} {cat.label}</Badge>}
                    {(() => { const p = getPriorityConfig("occurrence", detailOcc.priority || (detailOcc.urgency==="Emergência"?"emergency":detailOcc.urgency==="Urgente"?"urgent":detailOcc.urgency==="Alta"?"high":"normal")); return <Badge color={p.color}>{p.icon} {p.label}</Badge>; })()}
                  </div>
                  <div style={{fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.7,marginBottom:4}}>{detailOcc.recordNumber || detailOcc.id}</div>
                  <div style={{fontWeight:800,color:T.text,fontSize:18}}>{detailOcc.title}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:4}}>📅 {fmtDate(detailOcc.scheduledDate||detailOcc.createdAt||"")} · ⏱ {days}d</div>
                </div>
                {canEdit&&<Btn size="sm" variant="ghost" onClick={()=>{setForm({...detailOcc,_new:false,_returnToDetailId:detailOcc.id});setDetailOcc(null);}}>✏️ Editar</Btn>}
              </div>
              {detailOcc.description&&<div style={{fontSize:13,color:T.muted,background:T.card2,borderRadius:8,padding:"8px 12px",marginBottom:10}}>{detailOcc.description}</div>}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:8,marginBottom:10}}>
                {detailOcc.location&&<div style={{fontSize:12,color:T.muted}}>📍 <strong style={{color:T.text}}>{detailOcc.location}</strong></div>}
                <div style={{fontSize:12,color:T.muted}}>📅 <strong style={{color:T.text}}>{detailOcc.reportedBy} · {fmtDateTime(detailOcc.reportedAt||"")}</strong></div>
                {prov&&<div style={{fontSize:12,color:T.muted}}>👷 <strong style={{color:T.text}}>{prov.name}</strong>{prov.specialty?` · ${prov.specialty}`:""}</div>}
                {detailOcc.assignedTo&&<div style={{fontSize:12,color:T.muted}}>👤 <strong style={{color:T.text}}>{detailOcc.assignedTo}</strong></div>}
              </div>
              <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px",marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}><div style={{fontWeight:700,color:T.text,fontSize:13}}>🌦️ Condição climática operacional</div>{detailClimatePanel?.meta&&<Badge color={detailClimatePanel.meta.color}>{detailClimatePanel.meta.icon} {detailClimatePanel.meta.label}</Badge>}</div>
                {detailClimateLoading&&<div style={{fontSize:12,color:T.muted}}>Carregando leitura climática...</div>}
                {!detailClimateLoading&&detailClimatePanel?.error&&<div style={{fontSize:12,color:T.muted}}>{detailClimatePanel.error}</div>}
                {!detailClimateLoading&&!detailClimatePanel?.error&&detailClimatePanel&&<>
                  <div style={{fontSize:13,color:T.text,fontWeight:700,marginBottom:4}}>{detailClimatePanel.headline}</div>
                  <div style={{fontSize:12,color:T.muted,marginBottom:8}}>{detailClimatePanel.recommendation}</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>{detailClimatePanel.signals.map(signal=><React.Fragment key={signal.id}>{renderClimateToneBadge(signal.tone, `${signal.icon} ${signal.label}`, signal.value)}</React.Fragment>)}</div>
                  <div style={{fontSize:11,color:T.muted,lineHeight:1.45}}>📍 {detailClimatePanel.contextLabel} · 🛰️ {detailClimatePanel.sourceLabel}{detailClimatePanel.observedLabel?` · leitura ${fmtDate(detailClimatePanel.observedLabel)}`:""}</div>
                </>}
              </div>
              {detailOcc.budget?.value>0&&<div style={{background:`${T.green}12`,border:`1px solid ${T.green}33`,borderRadius:8,padding:"10px 14px",marginBottom:10}}><div style={{fontWeight:700,color:T.green,fontSize:13,marginBottom:4}}>💰 Orçamento</div><div style={{fontSize:12}}><strong style={{color:T.green}}>R$ {fmtMoney(detailOcc.budget.value)}</strong>{detailOcc.budget.date&&<span style={{color:T.muted}}> · {fmtDate(detailOcc.budget.date)}</span>}</div>{detailOcc.budget.notes&&<div style={{fontSize:11,color:T.muted,marginTop:4}}>{detailOcc.budget.notes}</div>}</div>}
              {canInteractOccurrence(detailOcc)&&!["Concluída","Cancelada"].includes(detailOcc.status)&&(
                <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginTop:12}}>
                  <Input type="date" value={occSchedDate||detailOcc.scheduledDate||""} onChange={e=>setOccSchedDate(e.target.value)} style={{maxWidth:180}}/>
                  <Btn size="sm" onClick={()=>{ const dt = occSchedDate||detailOcc.scheduledDate||today(); if(!dt){ show("Informe a data do agendamento.","error"); return; } updateOccurrence(detailOcc.id,{scheduledDate:dt,status:"Serviço Agendado"},"Ocorrência agendada",`Data: ${fmtDate(dt)}`); }}>📅 Agendar</Btn>
                  {!["Em Execução","Concluída","Cancelada"].includes(detailOcc.status)&&<Btn size="sm" onClick={()=>updateOccurrence(detailOcc.id,{startedAt:detailOcc.startedAt||new Date().toISOString(),status:"Em Execução"},detailOcc.status==="Pausada"?"Tarefa reiniciada":"Execução iniciada")}>{detailOcc.status==="Pausada"?"▶️ Recomeçar":"⚡ Iniciar"}</Btn>}
                  {detailOcc.status==="Em Execução"&&<Btn size="sm" variant="ghost" onClick={()=>setOccPauseForm({id:detailOcc.id,reason:(pauseReasonOptions[0]||""),obs:""})}>⏸️ Pausar Tarefa</Btn>}
                  <Btn size="sm" variant="ghost" onClick={()=>setOccMatForm({id:detailOcc.id,items:[{stockId:"",qtySeparated:"",qtyUsed:""}]})}>📦 Adicionar Materiais</Btn>
                  {(canAddPhotosToOccurrence(detailOcc) || getOccurrencePhotoEntries(detailOcc).length>0)&&<Btn size="sm" variant="ghost" onClick={()=>canAddPhotosToOccurrence(detailOcc)?setOccPhotoForm({occId:detailOcc.id}):null} disabled={!canAddPhotosToOccurrence(detailOcc)}><span style={{display:"inline-flex",alignItems:"center",gap:6}}><ServicePhotoAddPhoneIcon size={16}/> Fotos {getOccurrencePhotoEntries(detailOcc).length>0?`(${getOccurrencePhotoEntries(detailOcc).length})`:""}</span></Btn>}
                  {detailOcc.status!=="Concluída"&&<Btn size="sm" onClick={()=>updateOccurrence(detailOcc.id,{completionDate:today(),status:"Concluída"},"Ocorrência concluída")}>✅ Concluir</Btn>}
                </div>
              )}
              {occPauseForm&&String(occPauseForm.id)===String(detailOcc.id)&&<OccurrenceInlinePanel title="⏸️ Pausar Tarefa" onClose={()=>setOccPauseForm(null)}><div style={{display:"flex",flexDirection:"column",gap:12}}><Field label="Justificativa *"><Select value={occPauseForm.reason||""} onChange={e=>setOccPauseForm(prev=>({...prev,reason:e.target.value}))}>{(pauseReasonOptions||[]).map(r=><option key={r}>{r}</option>)}</Select></Field><Field label="Observação"><Textarea rows={3} value={occPauseForm.obs||""} onChange={e=>setOccPauseForm(prev=>({...prev,obs:e.target.value}))} placeholder="Detalhes adicionais (opcional)"/></Field><div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={()=>setOccPauseForm(null)}>Cancelar</Btn><Btn onClick={()=>{ if(!(occPauseForm.reason||"").trim()){ show("Selecione a justificativa da pausa.","error"); return; } updateOccurrence(occPauseForm.id,{status:"Pausada",pausedAt:new Date().toISOString(),pauseReason:occPauseForm.reason,pauseNote:occPauseForm.obs||""},"Tarefa pausada",`${occPauseForm.reason}${occPauseForm.obs?` — ${occPauseForm.obs}`:""}`); setOccPauseForm(null); }}>Salvar Pausa</Btn></div></div></OccurrenceInlinePanel>}
              {occMatForm&&String(occMatForm.id)===String(detailOcc.id)&&<OccurrenceInlinePanel title="📦 Adicionar Materiais" onClose={()=>setOccMatForm(null)}><div style={{display:"flex",flexDirection:"column",gap:14}}><div style={{background:`${T.yellow}15`,border:`1px solid ${T.yellow}44`,borderRadius:8,padding:"10px 12px",fontSize:12,color:T.yellow,lineHeight:1.5}}>⚠️ A quantidade separada sai do estoque imediatamente. A quantidade utilizada pode ser informada depois. Se ela não for informada, a baixa considerada será a quantidade separada.</div>{(() => { const items = Array.isArray(occMatForm.items) && occMatForm.items.length ? occMatForm.items : [{stockId:"",qtySeparated:"",qtyUsed:""}]; return <><div style={{display:"flex",flexDirection:"column",gap:10}}>{items.map((item,i)=>{ const selected = (enrichedStockItems||[]).find(s=>String(s.id)===String(item.stockId)); return <div key={i} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12,display:"flex",flexDirection:"column",gap:10}}><div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1.4fr 110px 110px",gap:8,alignItems:"flex-end"}}><Field label="Produto"><Select value={item.stockId||""} onChange={e=>setOccMatForm(prev=>({...prev,items:(prev.items||items).map((x,j)=>j===i?{...x,stockId:e.target.value}:x)}))}><option value="">Selecione...</option>{(enrichedStockItems||[]).map(s=><option key={s.id} value={s.id}>{s.name} (saldo: {s.quantity||0} {s.unit})</option>)}</Select></Field><Field label="Separada"><Input type="number" min="0.001" step="0.001" value={item.qtySeparated||""} onChange={e=>setOccMatForm(prev=>({...prev,items:(prev.items||items).map((x,j)=>j===i?{...x,qtySeparated:e.target.value}:x)}))}/></Field><Field label="Utilizada (pode informar depois)"><Input type="number" min="0" step="0.001" value={item.qtyUsed||""} onChange={e=>setOccMatForm(prev=>({...prev,items:(prev.items||items).map((x,j)=>j===i?{...x,qtyUsed:e.target.value}:x)}))} placeholder={selected?.unit||""}/></Field></div><button onClick={()=>setOccMatForm(prev=>{ const base=(prev.items||items); return {...prev,items:base.length===1?base:base.filter((_,j)=>j!==i)}; })} style={{alignSelf:"stretch",background:`${T.red}18`,border:`1px solid ${T.red}33`,color:T.red,cursor:"pointer",fontSize:13,fontWeight:700,borderRadius:10,padding:"10px 12px"}}>Excluir item</button></div>;})}</div><Btn size="sm" variant="ghost" onClick={()=>setOccMatForm(prev=>({...prev,items:[...((prev.items||items)),{stockId:"",qtySeparated:"",qtyUsed:""}]}))}>+ Item</Btn></>; })()}<div style={{display:"flex",gap:10,justifyContent:"flex-end"}}><Btn variant="ghost" onClick={()=>setOccMatForm(null)}>Cancelar</Btn><Btn onClick={()=>saveOccurrenceMaterials(occMatForm.id, occMatForm.items||[{stockId:"",qtySeparated:"",qtyUsed:""}])}>📦 Confirmar</Btn></div></div></OccurrenceInlinePanel>}
              {occPhotoForm&&String(occPhotoForm.occId)===String(detailOcc.id)&&<OccurrencePhotosInline occId={detailOcc.id} onClose={()=>setOccPhotoForm(null)} />}
              {occRestockForm&&String(occRestockForm.id)===String(detailOcc.id)&&<OccurrenceInlinePanel title="Estorno de Materiais para Estoque" onClose={()=>setOccRestockForm(null)}><div style={{display:"flex",flexDirection:"column",gap:14}}><div style={{fontSize:13,color:T.text,fontWeight:700}}>{occRestockForm.title||"Ocorrência"}</div><div style={{fontSize:12,color:T.muted,lineHeight:1.45}}>Para excluir este registro, é necessário devolver ao estoque todos os materiais consumidos.</div><div style={{display:"grid",gap:8}}>{getRestockableMaterials(occRestockForm).map((m,idx)=><div key={`${m.stockId||'item'}_${idx}`} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:10,alignItems:"center",padding:"10px 12px",background:T.card2,border:`1px solid ${T.border}`,borderRadius:10}}><div><div style={{fontSize:13,fontWeight:700,color:T.text}}>{m.name||m.stockId}</div><div style={{fontSize:11,color:T.muted}}>Retorno integral ao estoque</div></div><div style={{fontSize:13,fontWeight:800,color:T.accent,whiteSpace:"nowrap"}}>{getMaterialUsageQty(m)} {m.unit || "un"}</div></div>)}</div><Field label="Observação do estorno" col="1/-1"><Textarea rows={2} value={occRestockForm.note||""} onChange={e=>setOccRestockForm(prev=>({...prev,note:e.target.value}))} placeholder="Opcional. Ex.: exclusão do registro após devolução dos materiais." /></Field><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,paddingTop:6,borderTop:`1px solid ${T.border}`}}><div style={{fontSize:12,color:T.muted}}>Total a devolver: <strong style={{color:T.text}}>{getRestockableMaterials(occRestockForm).reduce((sum,m)=>sum+getMaterialUsageQty(m),0)}</strong></div><div style={{display:"flex",gap:10}}><Btn variant="ghost" onClick={()=>setOccRestockForm(null)}>Cancelar</Btn><Btn onClick={()=>confirmOccurrenceMaterialRestock(occRestockForm, occRestockForm.note || "")}>Confirmar estorno</Btn></div></div></div></OccurrenceInlinePanel>}
            </div>
            {(detailOcc.materials||[]).length>0&&<div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 18px",marginBottom:14}}>
              <div style={{fontWeight:700,color:T.text,fontSize:14,marginBottom:12}}>📦 Materiais utilizados</div>
              {(detailOcc.materials||[]).some(m=>m?.stockId && (m.pendingUsage || m.qtyUsed===null || m.qtyUsed==="" || typeof m.qtyUsed==="undefined"))&&<div style={{background:`${T.yellow}15`,border:`1px solid ${T.yellow}44`,borderRadius:8,padding:"8px 12px",marginBottom:10,fontSize:12,color:T.yellow,lineHeight:1.45}}>⚠️ Há materiais com quantidade utilizada pendente. Se ela não for informada, valerá a quantidade separada já baixada do estoque.</div>}
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {(detailOcc.materials||[]).filter(m=>m.stockId&&(m.qtyUsed||m.qty||m.qtySeparated)).map((m,i)=>{ const st=enrichedStockItems.find(s=>String(s.id)===String(m.stockId)); const pending=!!(m.pendingUsage || m.qtyUsed===null || m.qtyUsed==="" || typeof m.qtyUsed==="undefined"); const draftKey=`${detailOcc.id}::${i}`; return <div key={i} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 12px"}}><div style={{fontSize:12,color:T.text,fontWeight:700,marginBottom:4}}>{m.name||st?.name||m.stockId}</div><div style={{fontSize:12,color:T.muted,marginBottom:pending?8:0}}>Separado {m.qtySeparated||m.qty||0} / utilizado {pending?"pendente":(m.qtyUsed??m.qty??m.qtySeparated)} {m.unit||st?.unit||""}</div>{pending&&<div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"160px auto",gap:8,alignItems:"end"}}><Field label="Quantidade utilizada"><Input type="number" min="0" step="0.001" value={occMaterialUsageDrafts[draftKey]??""} onChange={e=>setOccMaterialUsageDrafts(prev=>({...prev,[draftKey]:e.target.value}))} placeholder={m.unit||st?.unit||""}/></Field><Btn size="sm" onClick={()=>saveOccurrenceMaterialUsage(detailOcc.id, i, occMaterialUsageDrafts[draftKey])}>Salvar utilizada</Btn></div>}</div>; })}
              </div>
            </div>}
            {(((detailOcc.timeline||[]).length>0) || getOccurrencePhotoBatches(detailOcc).length>0)&&(
              <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 18px"}}>
                <div style={{fontWeight:700,color:T.text,fontSize:14,marginBottom:12}}>📋 Histórico</div>
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  {[...(detailOcc.timeline||[])].reverse().map((ev,i)=>(
                    <div key={i} style={{display:"flex",gap:10,alignItems:"stretch"}}>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"center",width:18,flexShrink:0}}><div style={{width:12,height:12,borderRadius:"50%",background:T.red,marginTop:4}}/>{i<(detailOcc.timeline||[]).length-1&&<div style={{width:2,flex:1,background:`${T.red}33`,marginTop:2}}/>}</div>
                      <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:"10px 12px",flex:1}}><div style={{fontWeight:700,color:T.text,fontSize:13,marginBottom:4}}>{ev.action}</div>{ev.note&&<div style={{fontSize:12,color:T.muted,marginBottom:4}}>{ev.note}</div>}<div style={{fontSize:10,color:T.muted}}>{ev.actor||"Usuário"}{ev.ts?` · ${new Date(ev.ts).toLocaleString("pt-BR")}`:""}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Form Modal */}
      {occPhotoLightbox&&<div onClick={()=>setOccPhotoLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.92)",zIndex:1400,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12,padding:16,cursor:"zoom-out"}}><img src={occPhotoLightbox.photos[occPhotoLightbox.idx]?.photo||occPhotoLightbox.photos[occPhotoLightbox.idx]?.url||occPhotoLightbox.photos[occPhotoLightbox.idx]?.thumb} alt="" style={{maxWidth:"92vw",maxHeight:"78vh",objectFit:"contain",borderRadius:12,boxShadow:"0 18px 40px rgba(0,0,0,.45)",cursor:"auto"}} onClick={e=>e.stopPropagation()}/><div onClick={e=>e.stopPropagation()} style={{display:"flex",alignItems:"center",gap:14,background:"rgba(15,23,42,.78)",border:"1px solid rgba(255,255,255,.12)",borderRadius:999,padding:"8px 14px"}}>{occPhotoLightbox.photos.length>1&&<><button onClick={()=>setOccPhotoLightbox(lb=>({...lb,idx:(lb.idx-1+lb.photos.length)%lb.photos.length}))} style={{background:"none",border:"none",color:"#fff",fontSize:28,cursor:"pointer",lineHeight:1}}>‹</button><span style={{color:"#E5E7EB",fontSize:12,minWidth:60,textAlign:"center"}}>{occPhotoLightbox.idx+1}/{occPhotoLightbox.photos.length}</span><button onClick={()=>setOccPhotoLightbox(lb=>({...lb,idx:(lb.idx+1)%lb.photos.length}))} style={{background:"none",border:"none",color:"#fff",fontSize:28,cursor:"pointer",lineHeight:1}}>›</button></>}<button onClick={()=>setOccPhotoLightbox(null)} style={{background:"none",border:"none",color:"#fff",fontSize:22,cursor:"pointer",lineHeight:1}}>×</button></div></div>}

      {form&&(
        <div>
          <button
            onClick={closeOccurrenceForm}
            style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}
          >
            ← Voltar
          </button>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}>
            <div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:14}}>
              {form?(form._new?"Nova Ocorrência":"Editar Ocorrência"):""}
            </div>
            <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
              <Field label="Título *" col="1/-1">
                <Select value={form.title||""} onChange={e=>setForm(f=>({...f,title:e.target.value}))}>
                  <option value="">Selecione o título da ocorrência...</option>
                  {occTitleOptions.map(t=><option key={t}>{t}</option>)}
                </Select>
              </Field>
              <Field label="Categoria *">
                <Select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                  <option value="">Selecione...</option>
                  {OCCURRENCE_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </Select>
              </Field>
              <Field label="Prioridade" col={isCompactViewport()?"1/-1":undefined}>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {getPriorities("occurrence").map(p=>(
                    <div key={p.id} onClick={()=>setForm(f=>({...f,priority:p.id,urgency:p.label}))}
                      style={{flex:isCompactViewport()?"1 1 calc(50% - 6px)":"1 1 auto",padding:"6px 10px",borderRadius:8,cursor:"pointer",textAlign:"center",border:`2px solid ${(form.priority||"normal")===p.id?p.color:T.border}`,background:(form.priority||"normal")===p.id?`${p.color}18`:T.card2,transition:"all .15s"}}>
                      <div style={{fontSize:12,fontWeight:700,color:(form.priority||"normal")===p.id?p.color:T.text}}>{p.icon} {p.label}</div>
                    </div>
                  ))}
                </div>
              </Field>
              <Field label="Centro de Custo *">
                <Select value={form.costCenter||""} onChange={e=>setForm(f=>({...f,costCenter:e.target.value,costCenters:e.target.value?[e.target.value]:[]}))} disabled={bridgeScoped}>
                  <option value="">Selecione...</option>
                  {allowedCostCenters.map(cc=><option key={cc} value={cc}>{cc}</option>)}
                </Select>
                {!!bridgeScoped && <div style={{fontSize:11,color:T.muted,marginTop:4}}>Centro de custo travado pela ponte da Sede.</div>}
              </Field>
              <Field label="Localização / Pasto">
                <Select value={form.location||""} onChange={e=>setForm(f=>({...f,location:e.target.value,poiId:""}))}>
                  <option value="">Selecione o local...</option>
                  {pastures.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                </Select>
              </Field>
              <Field label="Ponto de Interesse">
                <Select value={form.poiId||""} onChange={e=>setForm(f=>({...f,poiId:e.target.value}))}>
                  <option value="">Sem POI</option>
                  {pois.map(p=><option key={p.id} value={p.id}>{p.name||p.label}</option>)}
                </Select>
              </Field>
              {(form.location || form.poiId) && <div style={{gridColumn:"1/-1",background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:"12px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}><div style={{fontWeight:700,color:T.text,fontSize:13}}>🌦️ Leitura climática para esta ocorrência</div>{formClimatePanel?.meta&&<Badge color={formClimatePanel.meta.color}>{formClimatePanel.meta.icon} {formClimatePanel.meta.label}</Badge>}</div>
                {formClimateLoading&&<div style={{fontSize:12,color:T.muted}}>Carregando leitura climática...</div>}
                {!formClimateLoading&&formClimatePanel?.error&&<div style={{fontSize:12,color:T.muted}}>{formClimatePanel.error}</div>}
                {!formClimateLoading&&!formClimatePanel?.error&&formClimatePanel&&<>
                  <div style={{fontSize:12,color:T.text,fontWeight:700,marginBottom:4}}>{formClimatePanel.headline}</div>
                  <div style={{fontSize:11,color:T.muted,marginBottom:8}}>{formClimatePanel.recommendation}</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{formClimatePanel.signals.map(signal=><React.Fragment key={signal.id}>{renderClimateToneBadge(signal.tone, `${signal.icon} ${signal.label}`, signal.value)}</React.Fragment>)}</div>
                </>}
              </div>}
              <Field label="Maquinário Envolvido">
                <Select value={form.machineId||""} onChange={e=>setForm(f=>({...f,machineId:e.target.value}))}>
                  <option value="">Sem maquinário</option>
                  {machinery.map(m=><option key={m.id} value={m.id}>{m.name}{m.plate?` (${m.plate})`:""}</option>)}
                </Select>
              </Field>
              <Field label="Tipo de Execução">
                <Select value={form.execType||""} onChange={e=>setForm(f=>({...f,execType:e.target.value}))}>
                  <option value="">Selecione...</option>
                  {execTypeOptions.map(t=><option key={t}>{t}</option>)}
                </Select>
              </Field>
              <Field label="Descrição detalhada" col="1/-1">
                <Textarea rows={3} value={form.description||""} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Descreva a ocorrência em detalhes..."/>
              </Field>

              <Field label="👷 Prestador de Serviço" col="1/-1">
                <Select value={form.providerId||""} onChange={e=>setForm(f=>({...f,providerId:e.target.value}))}>
                  <option value="">Nenhum / A definir</option>
                  {activeProviders.map(p=><option key={p.id} value={p.id}>👷 {p.name} — {p.specialty || p.serviceCategories?.[0] || "Prestador"}</option>)}
                </Select>
                {activeProviders.length===0&&<div style={{fontSize:11,color:T.yellow,marginTop:4}}>⚠️ Nenhum prestador cadastrado. Cadastre em Pessoas com o papel Prestador.</div>}
              </Field>

              <Field label="👤 Delegar contato para colaborador" col="1/-1">
                <Select value={form.assignedToId||""} onChange={e=>{ const emp=(employees||[]).find(x=>String(x.id)===String(e.target.value)); setForm(f=>({...f,assignedTo:emp?.name||"",assignedToId:emp?.id||"",assignedToUsername:emp?.username||""})); }}>
                  <option value="">Admin/Gestor fará o contato</option>
                  {employees.filter(e=>e.status==="Ativo"||e.active).map(e=>(
                    <option key={e.id} value={e.id}>{e.name} — {e.role||"Colaborador"}</option>
                  ))}
                </Select>
              </Field>

              <Field label="👥 Colaboradores participantes" col="1/-1">
                <Select value="" onChange={e=>{
                  const emp = (employees||[]).find(x=>String(x.id)===String(e.target.value));
                  if(!emp) return;
                  setForm(f=>{
                    const current = Array.isArray(f.assignees) ? f.assignees : [];
                    return current.some(v=>String((v&&typeof v==="object")?v.id:v)===String(emp.id)) ? f : {...f, assignees:[...current, { id:emp.id, name:emp.name, username:emp.username||"", employeeId:emp.id, linkedAccountId:emp.linkedAccountId||"", userId:emp.userId||"" }]};
                  });
                  e.target.value="";
                }}>
                  <option value="">+ Adicionar colaborador participante...</option>
                  {employees.filter(e=>e.status==="Ativo"||e.active).map(e=>(
                    <option key={e.id} value={e.id}>{e.name} — {e.role||"Colaborador"}</option>
                  ))}
                </Select>
                {(Array.isArray(form.assignees)?form.assignees:[]).length>0&&<div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
                  {(form.assignees||[]).map(v=>({ id:String((v&&typeof v==="object")?v.id:v), label:(v&&typeof v==="object")?(v.name||v.displayName||v.id):String(v||"") })).map(entry=>(
                    <span key={entry.id||entry.label} style={{fontSize:12,background:`${T.accent}18`,color:T.text,border:`1px solid ${T.accent}44`,borderRadius:999,padding:"6px 10px",display:"inline-flex",alignItems:"center",gap:8,fontWeight:600,lineHeight:1}}>
                      <span style={{color:T.accent,whiteSpace:"nowrap",maxWidth:220,overflow:"hidden",textOverflow:"ellipsis"}}>{entry.label}</span>
                      <button type="button" onClick={()=>setForm(f=>({...f,assignees:(f.assignees||[]).filter(v=>String((v&&typeof v==="object")?v.id:v)!==String(entry.id||entry.label))}))} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:14,padding:0,lineHeight:1,fontWeight:800}}>×</button>
                    </span>
                  ))}
                </div>}
              </Field>

              <div style={{gridColumn:"1/-1",borderTop:`1px solid ${T.border}`,paddingTop:14}}>
                <div style={{fontWeight:700,color:T.text,fontSize:13,marginBottom:10}}>💰 Orçamento</div>
                <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:12}}>
                  <Field label="Valor (R$)">
                    <Input type="number" min="0" step="0.01" value={form.budget?.value||0}
                      onChange={e=>setForm(f=>({...f,budget:{...f.budget,value:Number(e.target.value)}}))}/>
                  </Field>
                  <Field label="Data do Orçamento">
                    <Input type="date" value={form.budget?.date||""}
                      onChange={e=>setForm(f=>({...f,budget:{...f.budget,date:e.target.value}}))}/>
                  </Field>
                  <Field label="Observações do orçamento" col="1/-1">
                    <Input value={form.budget?.notes||""} placeholder="Condições, prazo, garantia..."
                      onChange={e=>setForm(f=>({...f,budget:{...f.budget,notes:e.target.value}}))}/>
                  </Field>
                  <Field label="📷 Foto do orçamento (opcional)" col="1/-1">
                    <input type="file" accept="image/*" multiple
                      onChange={e=>{
                        const files=Array.from(e.target.files);
                        Promise.all(files.map(f=>new Promise(res=>{
                          const r=new FileReader();r.onload=ev=>res(ev.target.result);r.readAsDataURL(f);
                        }))).then(photos=>setForm(f=>({...f,budget:{...f.budget,photos:[...(f.budget?.photos||[]),...photos]}})));
                      }}
                      style={{fontSize:12,color:T.muted,width:"100%"}}/>
                    {(form.budget?.photos||[]).length>0&&(
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>
                        {(form.budget.photos||[]).map((ph,i)=>(
                          <div key={i} style={{position:"relative"}}>
                            <img src={ph} alt="" style={{width:64,height:64,objectFit:"cover",borderRadius:8,border:`1px solid ${T.border}`}}/>
                            <button type="button" onClick={()=>setForm(f=>({...f,budget:{...f.budget,photos:f.budget.photos.filter((_,j)=>j!==i)}}))}
                              style={{position:"absolute",top:-4,right:-4,background:T.red,border:"none",borderRadius:"50%",width:18,height:18,color:"#fff",cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </Field>
                </div>
              </div>

              <Field label="📅 Data agendada para execução" col={isCompactViewport()?"1/-1":undefined}>
                <Input type="date" value={form.scheduledDate||""} onChange={e=>setForm(f=>({...f,scheduledDate:e.target.value}))}/>
              </Field>
              <Field label="Observações finais" col={isCompactViewport()?"1/-1":undefined}>
                <Input value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Notas adicionais..."/>
              </Field>

              <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"space-between",paddingTop:8,borderTop:`1px solid ${T.border}`}}>
                <div>{!form._new&&canDelete&&<Btn variant="danger" onClick={()=>requestOccurrenceDelete(form)}>🗑️ Excluir</Btn>}</div>
                <div style={{display:"flex",gap:10}}>
                  <Btn variant="ghost" onClick={closeOccurrenceForm}>Cancelar</Btn>
                  <Btn onClick={save}>Salvar</Btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}}